import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { LayoutDashboard, ShoppingCart, Package, Users, Truck, Settings, LogOut, ChevronDown, Search, Plus, Pencil, Trash2, Eye, X, RefreshCw, DollarSign, Clock, AlertCircle, CheckCircle, Tag, MapPin, Upload, Key, User, FileText, Shield, Megaphone, Ban, StickyNote, Star, Flag, Mail, CreditCard, BarChart3 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { AccountSettings } from '@/components/AccountSettings';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';

const STATUS_COLORS = { confirmed: 'bg-blue-500/20 text-blue-400', preparing: 'bg-yellow-500/20 text-yellow-400', ready: 'bg-purple-500/20 text-purple-400', out_for_delivery: 'bg-amber-500/20 text-amber-400', delivered: 'bg-green-500/20 text-green-400', cancelled: 'bg-red-500/20 text-red-400' };

export default function AdminPanel() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    if (!authLoading && (!user || !['admin', 'super_admin'].includes(user.role))) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  if (authLoading || !user) return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#cc3737] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#0B0E14]" data-testid="admin-panel">
      {/* Top Bar */}
      <nav className="fixed top-0 w-full z-50 bg-[#0B0E14]/90 backdrop-blur-xl border-b border-white/10 h-14">
        <div className="h-full px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#cc3737] flex items-center justify-center"><LayoutDashboard className="w-4 h-4 text-white" /></div>
            <span className="font-['Playfair_Display'] text-lg text-white font-medium">Admin Panel</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[#94A3B8] text-sm hidden sm:block">{user.email}</span>
            <button onClick={() => { logout(); navigate('/login'); }} className="text-[#64748B] hover:text-white transition-colors" data-testid="admin-logout"><LogOut className="w-4 h-4" /></button>
          </div>
        </div>
      </nav>

      <div className="pt-14">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col min-h-[calc(100vh-56px)]">
          {/* Tab Navigation */}
          <div className="border-b border-white/10 bg-[#0B0E14] px-6 overflow-x-auto">
            <TabsList className="bg-transparent h-12 gap-0">
              {[
                { value: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
                { value: 'analytics', icon: DollarSign, label: 'Analytics' },
                { value: 'orders', icon: ShoppingCart, label: 'Orders' },
                { value: 'products', icon: Package, label: 'Products' },
                { value: 'customers', icon: Users, label: 'Customers' },
                { value: 'drivers', icon: Truck, label: 'Drivers' },
                { value: 'staff', icon: Shield, label: 'Staff' },
                { value: 'promotions', icon: Tag, label: 'Promos' },
                { value: 'zones', icon: MapPin, label: 'Zones' },
                { value: 'categories', icon: Package, label: 'Categories' },
                { value: 'hours', icon: Clock, label: 'Hours' },
                { value: 'content', icon: FileText, label: 'Content' },
                { value: 'security', icon: Shield, label: 'Security' },
                { value: 'cash', icon: DollarSign, label: 'Cash' },
                { value: 'settings', icon: Settings, label: 'Settings' },
              ].map(tab => (
                <TabsTrigger key={tab.value} value={tab.value} className="data-[state=active]:bg-[#cc3737]/20 data-[state=active]:text-white text-[#64748B] rounded-lg px-4 gap-2" data-testid={`admin-tab-${tab.value}`}>
                  <tab.icon className="w-4 h-4" /> <span className="hidden sm:inline">{tab.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <div className="flex-1 p-6 overflow-auto">
            <TabsContent value="dashboard"><DashboardTab /></TabsContent>
            <TabsContent value="analytics"><AnalyticsTab /></TabsContent>
            <TabsContent value="orders"><OrdersTab /></TabsContent>
            <TabsContent value="products"><ProductsTab /></TabsContent>
            <TabsContent value="customers"><CustomersTab /></TabsContent>
            <TabsContent value="drivers"><DriversTab /></TabsContent>
            <TabsContent value="staff"><StaffTab /></TabsContent>
            <TabsContent value="promotions"><PromotionsTab /></TabsContent>
            <TabsContent value="zones"><ZonesTab /></TabsContent>
            <TabsContent value="categories"><CategoriesTab /></TabsContent>
            <TabsContent value="hours"><HoursTab /></TabsContent>
            <TabsContent value="content"><ContentTab /></TabsContent>
            <TabsContent value="security"><SecurityTab /></TabsContent>
            <TabsContent value="cash"><CashReconciliationTab /></TabsContent>
            <TabsContent value="settings"><SettingsTab /></TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboard(); }, []);
  const fetchDashboard = async () => {
    try { const res = await api.get('/admin/dashboard'); setData(res.data); } catch {} finally { setLoading(false); }
  };

  if (loading) return <LoadingSkeleton />;
  if (!data) return <p className="text-[#64748B]">Failed to load dashboard</p>;

  const kpis = [
    { label: "Today's Orders", value: data.today_orders, icon: ShoppingCart, color: '#3B82F6' },
    { label: "Today's Revenue", value: `$${data.today_revenue?.toFixed(2)}`, icon: DollarSign, color: '#10B981' },
    { label: "Average Order Value", value: `$${data.avg_order_value?.toFixed(2)}`, icon: DollarSign, color: '#10B981' },
    { label: "Completed Orders", value: data.completed_orders, icon: ShoppingCart, color: '#10B981' },
    { label: "Cancelled Orders", value: data.cancelled_orders, icon: X, color: '#EF4444' },
    { label: "Awaiting Delivery", value: data.awaiting_delivery, icon: Truck, color: '#F59E0B' },
    { label: 'Active Drivers', value: data.active_drivers, icon: Truck, color: '#b56b38' },
    { label: 'Pending Orders', value: data.pending_orders, icon: Clock, color: '#F59E0B' },
    { label: 'Out for Delivery', value: data.out_for_delivery, icon: Truck, color: '#8B5CF6' },
    { label: 'Total Products', value: data.total_products, icon: Package, color: '#cc3737' },
    { label: 'Total Customers', value: data.total_customers, icon: User, color: '#3B82F6' },
    { label: 'Avg Delivery Time', value: data.avg_delivery_time ? `${data.avg_delivery_time} min` : 'N/A', icon: Clock, color: '#8B5CF6' },
  ];

  const maxMonthVal = Math.max(1, ...(data.monthly_trend || []).map(m => m.revenue));
  const maxWeekVal = Math.max(1, ...(data.weekly_trend || []).map(w => w.sales));

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-center justify-between">
        <h2 className="font-['Playfair_Display'] text-2xl text-white">Dashboard Overview</h2>
        <button onClick={fetchDashboard} className="p-2 bg-[#12151C] border border-white/10 rounded-md text-[#94A3B8] hover:text-white" data-testid="refresh-dashboard"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {/* KPIs Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {kpis.map((kpi, i) => (
          <div key={i} className="bg-[#12151C] border border-white/10 rounded-xl p-4 transition-all hover:border-white/20" data-testid={`kpi-${i}`}>
            <kpi.icon className="w-5 h-5 mb-2" style={{ color: kpi.color }} />
            <p className="text-xl font-bold text-white font-mono">{kpi.value}</p>
            <p className="text-[#64748B] text-[11px] mt-1 font-medium">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Historical trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Trend */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white text-sm font-semibold mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#10B981]" /> Monthly Revenue Trend</h3>
          {(!data.monthly_trend || data.monthly_trend.length === 0) ? (
            <p className="text-[#64748B] text-xs py-4 text-center">No monthly trend data</p>
          ) : (
            <div className="flex items-end gap-3 h-40 pt-6">
              {data.monthly_trend.map((m, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <span className="absolute -top-6 text-white text-[9px] font-mono opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 px-1 py-0.5 rounded shadow">${m.revenue.toFixed(0)}</span>
                  <div className="w-full bg-gradient-to-t from-[#10B981] to-[#34D399] rounded-t transition-all hover:opacity-80" style={{ height: `${(m.revenue / maxMonthVal) * 100}%`, minHeight: '4px' }} />
                  <span className="text-[#64748B] text-[9px] font-mono mt-1 rotate-12">{m.month}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Weekly Sales Trend */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white text-sm font-semibold mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#3B82F6]" /> Weekly Sales Trend</h3>
          {(!data.weekly_trend || data.weekly_trend.length === 0) ? (
            <p className="text-[#64748B] text-xs py-4 text-center">No weekly trend data</p>
          ) : (
            <div className="flex items-end gap-2 h-40 pt-6">
              {data.weekly_trend.slice(-10).map((w, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <span className="absolute -top-6 text-white text-[9px] font-mono opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 px-1 py-0.5 rounded shadow">${w.sales.toFixed(0)}</span>
                  <div className="w-full bg-gradient-to-t from-[#3B82F6] to-[#60A5FA] rounded-t transition-all hover:opacity-80" style={{ height: `${(w.sales / maxWeekVal) * 100}%`, minHeight: '4px' }} />
                  <span className="text-[#64748B] text-[8px] font-mono mt-1 text-center truncate w-full">{w.week.split('-')[1]}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top statistics tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Top Selling Products */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white text-sm font-semibold mb-3">Top Products</h3>
          {(!data.top_products || data.top_products.length === 0) ? (
            <p className="text-[#64748B] text-xs py-4">No data</p>
          ) : (
            <div className="space-y-3">
              {data.top_products.map((p, i) => (
                <div key={i} className="flex justify-between items-center text-xs">
                  <span className="text-[#94A3B8] truncate flex-1">{i+1}. {p.name}</span>
                  <span className="text-[#64748B] text-[10px] font-mono mr-2">{p.quantity} sold</span>
                  <span className="text-white font-semibold font-mono">${p.revenue.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Categories */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white text-sm font-semibold mb-3">Top Categories</h3>
          {(!data.top_categories || data.top_categories.length === 0) ? (
            <p className="text-[#64748B] text-xs py-4">No data</p>
          ) : (
            <div className="space-y-3">
              {data.top_categories.map((c, i) => (
                <div key={i} className="flex justify-between items-center text-xs">
                  <span className="text-[#94A3B8]">{i+1}. {c.category}</span>
                  <span className="text-[#64748B] text-[10px] font-mono mr-2">{c.quantity} items</span>
                  <span className="text-white font-semibold font-mono">${c.revenue.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Low Stock Warning */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-1.5"><AlertCircle className="w-4 h-4 text-[#EF4444]" /> Inventory Alerts</h3>
          {(!data.low_stock || data.low_stock.length === 0) ? (
            <p className="text-[#10B981] text-xs py-4 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> All product stock healthy</p>
          ) : (
            <div className="space-y-3">
              {data.low_stock.map((p, i) => (
                <div key={i} className="flex justify-between items-center text-xs border-b border-white/5 pb-1">
                  <span className="text-[#94A3B8] truncate flex-1">{p.name}</span>
                  <span className={`font-bold font-mono px-2 py-0.5 rounded text-[10px] ${p.stock === 0 ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{p.stock} left</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Most Active Customers */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5 lg:col-span-1">
          <h3 className="text-white text-sm font-semibold mb-3">Top Customers</h3>
          {(!data.top_customers || data.top_customers.length === 0) ? (
            <p className="text-[#64748B] text-xs py-4">No active customers</p>
          ) : (
            <div className="space-y-3">
              {data.top_customers.map((c, i) => (
                <div key={i} className="flex flex-col text-xs border-b border-white/5 pb-2">
                  <div className="flex justify-between font-medium">
                    <span className="text-white">{c.name}</span>
                    <span className="text-[#10B981] font-mono">${c.total_spent.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#64748B] text-[10px] mt-0.5">
                    <span>{c.phone}</span>
                    <span>{c.order_count} orders</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Coupon Usage stats */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5 lg:col-span-1">
          <h3 className="text-white text-sm font-semibold mb-3">Coupon Usage</h3>
          {(!data.coupon_usage || data.coupon_usage.length === 0) ? (
            <p className="text-[#64748B] text-xs py-4">No coupons used</p>
          ) : (
            <div className="space-y-3">
              {data.coupon_usage.map((c, i) => (
                <div key={i} className="flex justify-between items-center text-xs">
                  <span className="text-[#b56b38] font-mono font-medium">{c.code}</span>
                  <span className="text-[#64748B] text-[10px]">{c.times_used} uses</span>
                  <span className="text-white font-mono">${c.total_discount.toFixed(2)} saved</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Orders */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5 lg:col-span-1">
          <h3 className="text-white text-sm font-semibold mb-3">Recent Activity</h3>
          <div className="space-y-3">
            {data.recent_orders?.slice(0, 5).map((order, i) => (
              <div key={i} className="flex justify-between items-center text-xs border-b border-white/5 pb-2">
                <div>
                  <p className="text-white font-medium">{order.order_number}</p>
                  <p className="text-[#64748B] text-[10px]">{new Date(order.created_at).toLocaleTimeString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-[#b56b38] font-mono font-semibold">${order.total?.toFixed(2)}</p>
                  <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${STATUS_COLORS[order.status] || ''}`}>{order.status}</span>
                </div>
              </div>
            ))}
            {(!data.recent_orders || data.recent_orders.length === 0) && <p className="text-[#64748B] text-xs py-4">No recent activity</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function AnalyticsTab() {
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/admin/analytics?period=${period}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(e => { if (!cancelled) toast.error(e.response?.data?.detail || 'Failed to load analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  if (loading) return <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="bg-[#12151C] rounded-xl h-24 animate-pulse" />)}</div>;
  if (!data) return <div className="text-[#94A3B8]">No data.</div>;

  const dailyEntries = Object.entries(data.daily_revenue || {}).sort(([a],[b]) => a.localeCompare(b));
  const maxDaily = Math.max(1, ...dailyEntries.map(([,v]) => v));
  const catEntries = Object.entries(data.category_revenue || {}).sort(([,a],[,b]) => b - a);
  const maxCat = Math.max(1, ...catEntries.map(([,v]) => v));
  const hourEntries = Array.from({length: 24}, (_, h) => [String(h).padStart(2,'0'), (data.hourly_distribution || {})[String(h).padStart(2,'0')] || 0]);
  const maxHour = Math.max(1, ...hourEntries.map(([,v]) => v));

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      {/* Period selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-white font-medium text-lg">Sales Analytics</h2>
        <div className="flex gap-1 bg-[#12151C] border border-white/10 rounded-full p-1">
          {[
            { v: 'day', l: 'Today' },
            { v: 'week', l: 'Last 7 days' },
            { v: 'month', l: 'This month' },
          ].map(p => (
            <button key={p.v} onClick={() => setPeriod(p.v)} className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${period === p.v ? 'bg-[#cc3737] text-white' : 'text-[#94A3B8] hover:text-white'}`} data-testid={`analytics-period-${p.v}`}>
              {p.l}
            </button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5" data-testid="kpi-revenue">
          <p className="text-[#64748B] text-xs uppercase tracking-wide mb-2">Revenue</p>
          <p className="text-white font-['Playfair_Display'] text-3xl">${data.total_revenue?.toFixed(2)}</p>
        </div>
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5" data-testid="kpi-orders">
          <p className="text-[#64748B] text-xs uppercase tracking-wide mb-2">Orders</p>
          <p className="text-white font-['Playfair_Display'] text-3xl">{data.total_orders}</p>
        </div>
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5" data-testid="kpi-aov">
          <p className="text-[#64748B] text-xs uppercase tracking-wide mb-2">Avg order value</p>
          <p className="text-white font-['Playfair_Display'] text-3xl">${data.avg_order_value?.toFixed(2)}</p>
        </div>
      </div>

      {/* Daily revenue bars */}
      {dailyEntries.length > 0 && (
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-medium mb-4">Daily revenue</h3>
          <div className="flex items-end gap-2 h-40">
            {dailyEntries.map(([day, rev]) => (
              <div key={day} className="flex-1 flex flex-col items-center gap-1 group">
                <span className="text-[#b56b38] text-[10px] font-mono opacity-0 group-hover:opacity-100 transition-opacity">${rev.toFixed(0)}</span>
                <div className="w-full bg-gradient-to-t from-[#cc3737] to-[#b56b38] rounded-t-md transition-all" style={{height: `${(rev / maxDaily) * 100}%`, minHeight: rev > 0 ? '4px' : '0'}} />
                <span className="text-[#64748B] text-[10px] font-mono">{day.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top products */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5" data-testid="top-products-card">
          <h3 className="text-white font-medium mb-4">Top products</h3>
          {(!data.top_products || data.top_products.length === 0) ? (
            <p className="text-[#64748B] text-sm">No sales yet in this period.</p>
          ) : (
            <ol className="space-y-2">
              {data.top_products.map(([name, qty], i) => (
                <li key={name} className="flex items-center justify-between text-sm">
                  <span className="text-[#94A3B8] truncate flex-1"><span className="text-[#b56b38] font-mono mr-2">{i + 1}.</span>{name}</span>
                  <span className="text-white font-mono">{qty}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Category revenue */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5" data-testid="category-revenue-card">
          <h3 className="text-white font-medium mb-4">Revenue by category</h3>
          {catEntries.length === 0 ? (
            <p className="text-[#64748B] text-sm">No data.</p>
          ) : (
            <div className="space-y-3">
              {catEntries.map(([cat, rev]) => (
                <div key={cat}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-[#94A3B8]">{cat}</span>
                    <span className="text-white font-mono">${rev.toFixed(2)}</span>
                  </div>
                  <div className="bg-[#0B0E14] rounded-full h-2 overflow-hidden">
                    <div className="bg-gradient-to-r from-[#b56b38] to-[#cc3737] h-full transition-all" style={{width: `${(rev / maxCat) * 100}%`}} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hourly distribution */}
      <div className="bg-[#12151C] border border-white/10 rounded-xl p-5" data-testid="hourly-card">
        <h3 className="text-white font-medium mb-4">Order volume by hour of day</h3>
        <div className="flex items-end gap-1 h-32">
          {hourEntries.map(([h, count]) => (
            <div key={h} className="flex-1 flex flex-col items-center gap-1 group">
              <span className="text-[#b56b38] text-[10px] font-mono opacity-0 group-hover:opacity-100">{count}</span>
              <div className="w-full bg-[#b56b38]/60 hover:bg-[#b56b38] rounded-t-sm transition-colors" style={{height: `${(count / maxHour) * 100}%`, minHeight: count > 0 ? '3px' : '0'}} />
              <span className="text-[#64748B] text-[9px] font-mono">{h}</span>
            </div>
          ))}
        </div>
        <p className="text-[#64748B] text-[11px] mt-2">Hours in UTC. Useful for planning driver shifts.</p>
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOrders = useCallback(async () => {
    try { const res = await api.get('/admin/orders', { params: statusFilter ? { status: statusFilter } : {} }); setOrders(res.data.orders); } catch {} finally { setLoading(false); }
  }, [statusFilter]);
  const fetchDrivers = useCallback(async () => {
    try { const res = await api.get('/admin/drivers'); setDrivers(res.data.drivers); } catch {}
  }, []);
  useEffect(() => { fetchOrders(); fetchDrivers(); }, [fetchOrders, fetchDrivers]);

  const handleStatusChange = async (orderId, newStatus) => {
    try { await api.put(`/orders/${orderId}/status`, { status: newStatus }); toast.success('Status updated'); fetchOrders(); } catch (e) { toast.error('Failed to update'); }
  };

  const handleAssignDriver = async (orderId, driverId) => {
    try { await api.put(`/orders/${orderId}/assign`, { driver_id: driverId }); toast.success('Driver assigned'); fetchOrders(); } catch (e) { toast.error('Failed to assign'); }
  };

  const filteredOrders = orders.filter(o => {
    const term = searchTerm.toLowerCase();
    return (o.order_number || '').toLowerCase().includes(term) ||
           (o.customer_name || '').toLowerCase().includes(term) ||
           (o.customer_phone || '').toLowerCase().includes(term);
  });

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Playfair_Display'] text-2xl text-white">Orders</h2>
        <div className="flex gap-2">
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search Order #, customer..."
            className="bg-[#12151C] border border-white/10 rounded-md px-3 py-1.5 text-sm text-white focus:outline-none focus:border-[#b56b38]/40"
            data-testid="order-search-input"
          />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-[#12151C] border border-white/10 rounded-md px-3 py-1.5 text-sm text-white" data-testid="order-status-filter">
            <option value="">All Statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="preparing">Preparing</option>
            <option value="ready">Ready</option>
            <option value="out_for_delivery">Out for Delivery</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button onClick={fetchOrders} className="p-2 bg-[#12151C] border border-white/10 rounded-md text-[#94A3B8] hover:text-white" data-testid="refresh-orders"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="bg-[#12151C] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-orders-table">
            <thead><tr className="border-b border-white/5">
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Order #</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Customer</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Items</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Total</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Status</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Driver</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Actions</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Priority</th>
            </tr></thead>
            <tbody>
              {filteredOrders.map((order, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-[#b56b38] font-medium">{order.order_number}</td>
                  <td className="px-4 py-3 text-white">{order.customer_name || 'Guest'}</td>
                  <td className="px-4 py-3 text-[#94A3B8]">{order.items?.length} items</td>
                  <td className="px-4 py-3 text-white">${order.total?.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <select value={order.status} onChange={e => handleStatusChange(order.id, e.target.value)} className="bg-transparent text-white text-xs border border-white/10 rounded px-2 py-1" data-testid={`order-status-select-${i}`}>
                      {['confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered', 'cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    {order.driver_name ? (
                      <span className="text-[#10B981] text-xs">{order.driver_name}</span>
                    ) : (
                      <select onChange={e => e.target.value && handleAssignDriver(order.id, e.target.value)} className="bg-transparent text-[#94A3B8] text-xs border border-white/10 rounded px-2 py-1" defaultValue="" data-testid={`assign-driver-${i}`}>
                        <option value="">Assign...</option>
                        {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setSelectedOrder(order)} className="text-[#94A3B8] hover:text-white" data-testid={`view-order-${i}`}><Eye className="w-4 h-4" /></button>
                  </td>
                  <td className="px-4 py-3">
                    <select value={order.priority || 'normal'} onChange={async e => { try { await api.put(`/orders/${order.id}/priority`, {priority: e.target.value}); fetchOrders(); } catch (err) { toast.error(err.response?.data?.detail || 'Could not change priority'); } }} className="bg-transparent text-xs border border-white/10 rounded px-1.5 py-0.5 text-white" data-testid={`order-priority-${i}`}>
                      <option value="normal">Normal</option><option value="rush">Rush</option><option value="vip">VIP</option><option value="fragile">Fragile</option>
                    </select>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#64748B]">No orders found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setSelectedOrder(null)}>
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-['Playfair_Display'] text-xl text-white">Order {selectedOrder.order_number}</h3>
              <button onClick={() => setSelectedOrder(null)} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-[#64748B]">Customer</span><span className="text-white">{selectedOrder.customer_name || 'Guest'}</span></div>
              <div className="flex justify-between"><span className="text-[#64748B]">Phone</span><span className="text-white">{selectedOrder.customer_phone || 'N/A'}</span></div>
              <div className="flex justify-between"><span className="text-[#64748B]">Address</span><span className="text-white text-right max-w-[200px]">{selectedOrder.delivery_address}</span></div>
              <div className="flex justify-between"><span className="text-[#64748B]">Status</span><span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[selectedOrder.status]}`}>{selectedOrder.status}</span></div>
              <div className="flex justify-between"><span className="text-[#64748B]">Total</span><span className="text-[#b56b38] font-medium">${selectedOrder.total?.toFixed(2)}</span></div>
              {selectedOrder.notes && <div><span className="text-[#64748B] block mb-1">Notes</span><span className="text-white">{selectedOrder.notes}</span></div>}
              {/* Pickup OTP - for store staff */}
              {selectedOrder.pickup_otp && !selectedOrder.pickup_otp_verified && (
                <div className="bg-[#b56b38]/10 border border-[#b56b38]/30 rounded-lg p-3 text-center">
                  <p className="text-[#94A3B8] text-xs mb-1">Pickup OTP (give to driver)</p>
                  <p className="text-white text-3xl font-mono font-bold tracking-[0.3em]" data-testid="admin-pickup-otp">{selectedOrder.pickup_otp}</p>
                </div>
              )}
              {selectedOrder.pickup_otp_verified && <p className="text-[#10B981] text-xs">Pickup verified</p>}
              {selectedOrder.delivery_otp_verified && <p className="text-[#10B981] text-xs">Delivery verified</p>}
              {/* Proof Photos */}
              {(selectedOrder.pickup_photo || selectedOrder.delivery_photo) && (
                <div className="grid grid-cols-2 gap-2">
                  {selectedOrder.pickup_photo && <div><p className="text-[#64748B] text-xs mb-1">Pickup Proof</p><img src={selectedOrder.pickup_photo} alt="" className="w-full h-20 object-cover rounded" /></div>}
                  {selectedOrder.delivery_photo && <div><p className="text-[#64748B] text-xs mb-1">Delivery Proof</p><img src={selectedOrder.delivery_photo} alt="" className="w-full h-20 object-cover rounded" /></div>}
                </div>
              )}
              <div className="border-t border-white/5 pt-3">
                <p className="text-[#64748B] mb-2">Items:</p>
                {selectedOrder.items?.map((item, j) => (
                  <div key={j} className="flex justify-between py-1"><span className="text-white">{item.quantity}x {item.name}</span><span className="text-[#94A3B8]">${(item.price * item.quantity).toFixed(2)}</span></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  
  // Search & Filtering (Server-driven)
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [stockFilter, setStockFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortKey, setSortKey] = useState('newest');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Selection states
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Bulk operation modal states
  const [showBulkPrice, setShowBulkPrice] = useState(false);
  const [showBulkInventory, setShowBulkInventory] = useState(false);
  const [bulkPriceLoading, setBulkPriceLoading] = useState(false);
  const [bulkPricePreviews, setBulkPricePreviews] = useState([]);
  const [bulkPriceForm, setBulkPriceForm] = useState({ mode: 'percent', direction: 'increase', value: '', target: 'price', roundRule: 'none' });
  const [bulkInventoryForm, setBulkInventoryForm] = useState({ mode: 'add', value: '', reason: '' });

  // Import CSV states
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Form fields
  const [form, setForm] = useState({
    name: '',
    brand: '',
    description: '',
    category: 'Liquor & Spirits',
    subcategory: '',
    price: '',
    sale_price: '',
    cost_price: '',
    stock: '',
    age_restricted: false,
    featured: false,
    visible: true,
    images: [''],
    sizes: [''],
    variants: [],
    abv: '',
    proof: '',
    sku: '',
    barcode: '',
    country: '',
    region: '',
    seo_title: '',
    seo_description: ''
  });

  // Debounce search term changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearch,
        category: categoryFilter,
        stock: stockFilter,
        status: statusFilter,
        sort_by: sortKey
      };
      const res = await api.get('/admin/products', { params });
      setProducts(res.data.products);
      setTotalProducts(res.data.total);
      setTotalPages(res.data.pages);
      
      const catRes = await api.get('/admin/categories');
      setCategories(catRes.data.categories);
    } catch (e) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearch, categoryFilter, stockFilter, statusFilter, sortKey]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const resetForm = () => {
    setForm({
      name: '',
      brand: '',
      description: '',
      category: categories[0]?.name || 'Liquor & Spirits',
      subcategory: '',
      price: '',
      sale_price: '',
      cost_price: '',
      stock: '',
      age_restricted: false,
      featured: false,
      visible: true,
      images: [''],
      sizes: [''],
      variants: [],
      abv: '',
      proof: '',
      sku: '',
      barcode: '',
      country: '',
      region: '',
      seo_title: '',
      seo_description: ''
    });
    setEditProduct(null);
  };

  const handleEdit = (p) => {
    setForm({
      ...p,
      price: String(p.price || ''),
      sale_price: p.sale_price !== null && p.sale_price !== undefined ? String(p.sale_price) : '',
      cost_price: p.cost_price !== null && p.cost_price !== undefined ? String(p.cost_price) : '',
      stock: String(p.stock || 0),
      abv: p.abv !== null && p.abv !== undefined ? String(p.abv) : '',
      proof: p.proof !== null && p.proof !== undefined ? String(p.proof) : '',
      images: p.images?.length ? p.images : [''],
      sizes: p.sizes?.length ? p.sizes : [''],
      variants: p.variants?.length ? p.variants.map(v => ({
        name: v.name || '',
        price: String(v.price || ''),
        sku: v.sku || '',
        barcode: v.barcode || '',
        description: v.description || ''
      })) : [],
      sku: p.sku || '',
      barcode: p.barcode || '',
      subcategory: p.subcategory || '',
      country: p.country || '',
      region: p.region || '',
      seo_title: p.seo_title || '',
      seo_description: p.seo_description || ''
    });
    setEditProduct(p);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0.0,
      sale_price: form.sale_price ? parseFloat(form.sale_price) : null,
      cost_price: form.cost_price ? parseFloat(form.cost_price) : null,
      stock: parseInt(form.stock) || 0,
      abv: form.abv ? parseFloat(form.abv) : null,
      proof: form.proof ? parseFloat(form.proof) : null,
      images: form.images.filter(Boolean),
      sizes: form.sizes.filter(Boolean),
      variants: form.variants.filter(v => v.name && v.price).map(v => ({
        ...v,
        price: parseFloat(v.price) || 0.0,
        sku: v.sku || null,
        barcode: v.barcode || null
      }))
    };
    try {
      if (editProduct) {
        await api.put(`/admin/products/${editProduct.id}`, payload);
        toast.success('Product updated');
      } else {
        await api.post('/admin/products', payload);
        toast.success('Product created');
      }
      setShowForm(false);
      resetForm();
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success('Deleted');
      fetchProducts();
    } catch {
      toast.error('Failed to delete product');
    }
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(products.map(p => p.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  // Bulk actions handlers
  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} selected products?`)) return;
    try {
      await api.post('/admin/products/bulk-delete', { ids: Array.from(selectedIds) });
      toast.success('Selected products deleted successfully');
      setSelectedIds(new Set());
      fetchProducts();
    } catch {
      toast.error('Bulk deletion failed');
    }
  };

  const handleBulkStatus = async (visible) => {
    try {
      await api.post('/admin/products/bulk-status', { ids: Array.from(selectedIds), visible });
      toast.success(`Updated status for ${selectedIds.size} products`);
      setSelectedIds(new Set());
      fetchProducts();
    } catch {
      toast.error('Bulk status update failed');
    }
  };

  const fetchBulkPricePreview = async () => {
    setBulkPriceLoading(true);
    try {
      const payload = {
        ids: Array.from(selectedIds),
        mode: bulkPriceForm.mode,
        value: parseFloat(bulkPriceForm.value) || 0.0,
        direction: bulkPriceForm.direction,
        target: bulkPriceForm.target,
        round_rule: bulkPriceForm.roundRule
      };
      const res = await api.post('/admin/products/bulk-price/preview', payload);
      setBulkPricePreviews(res.data.previews);
    } catch {
      toast.error('Failed to calculate pricing preview');
    } finally {
      setBulkPriceLoading(false);
    }
  };

  const handleBulkPriceApply = async () => {
    try {
      const payload = {
        ids: Array.from(selectedIds),
        mode: bulkPriceForm.mode,
        value: parseFloat(bulkPriceForm.value) || 0.0,
        direction: bulkPriceForm.direction,
        target: bulkPriceForm.target,
        round_rule: bulkPriceForm.roundRule
      };
      await api.post('/admin/products/bulk-price/apply', payload);
      toast.success('Pricing update applied successfully');
      setShowBulkPrice(false);
      setBulkPricePreviews([]);
      setBulkPriceForm({ mode: 'percent', direction: 'increase', value: '', target: 'price', roundRule: 'none' });
      setSelectedIds(new Set());
      fetchProducts();
    } catch {
      toast.error('Failed to apply bulk pricing updates');
    }
  };

  const handleBulkInventoryApply = async () => {
    if (!bulkInventoryForm.value) return toast.error('Please enter a quantity value');
    try {
      const payload = {
        ids: Array.from(selectedIds),
        mode: bulkInventoryForm.mode,
        value: parseInt(bulkInventoryForm.value) || 0,
        reason: bulkInventoryForm.reason || 'Bulk Stock Adjustment'
      };
      await api.post('/admin/products/bulk-inventory', payload);
      toast.success('Stock levels adjusted successfully');
      setShowBulkInventory(false);
      setBulkInventoryForm({ mode: 'add', value: '', reason: '' });
      setSelectedIds(new Set());
      fetchProducts();
    } catch {
      toast.error('Failed to apply inventory adjustments');
    }
  };

  // CSV Import/Export
  const handleExportCsv = async (mode = 'all') => {
    try {
      let params = {};
      if (mode === 'selected') {
        if (selectedIds.size === 0) return toast.error('No products selected');
        params.ids = Array.from(selectedIds).join(',');
      } else if (mode === 'filtered') {
        params = {
          search: debouncedSearch,
          category: categoryFilter,
          stock: stockFilter,
          status: statusFilter
        };
      }
      const res = await api.get('/admin/products/export', { params, responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `products_export_${mode}_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(blobUrl);
      toast.success('Export downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  const downloadSampleCsv = async () => {
    try {
      const res = await api.get('/admin/products/import/sample', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'products_sample.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Sample CSV downloaded');
    } catch { toast.error('Download failed'); }
  };

  const submitImport = async () => {
    if (!importFile) return toast.error('Please select a CSV file first');
    if (!importFile.name.toLowerCase().endsWith('.csv')) return toast.error('File must be .csv');
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await api.post('/admin/products/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImportResult(res.data);
      const { created, updated, skipped } = res.data;
      toast.success(`Imported: ${created} created, ${updated} updated, ${skipped} skipped`);
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadImportReport = async (importId) => {
    try {
      const res = await api.get(`/admin/products/import/history/${importId}/report`);
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(res.data, null, 2));
      const a = document.createElement('a');
      a.setAttribute("href", dataStr);
      a.setAttribute("download", `import_report_${importId}.json`);
      a.click();
    } catch {
      toast.error('Failed to download report');
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h2 className="font-['Playfair_Display'] text-2xl text-white">Product Catalog ({totalProducts})</h2>
          <p className="text-xs text-[#94A3B8] mt-1">Manage physical store inventory, pricing structures, and dynamic product catalogs.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* CSV Export Dropdown */}
          <div className="relative group">
            <button className="bg-[#12151C] border border-white/10 hover:border-[#b56b38]/40 text-[#94A3B8] hover:text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#b56b38]" /> Export CSV <ChevronDown className="w-3 h-3" />
            </button>
            <div className="absolute right-0 top-full mt-1 w-44 bg-[#12151C] border border-white/10 rounded-lg shadow-xl py-1 hidden group-hover:block z-20">
              <button onClick={() => handleExportCsv('all')} className="w-full text-left px-4 py-2 text-xs text-[#94A3B8] hover:text-white hover:bg-white/5 transition-colors">Export All Products</button>
              <button onClick={() => handleExportCsv('filtered')} className="w-full text-left px-4 py-2 text-xs text-[#94A3B8] hover:text-white hover:bg-white/5 transition-colors">Export Filtered List</button>
              <button onClick={() => handleExportCsv('selected')} className="w-full text-left px-4 py-2 text-xs text-[#94A3B8] hover:text-white hover:bg-white/5 transition-colors">Export Selected ({selectedIds.size})</button>
            </div>
          </div>
          
          <button onClick={() => { setImportFile(null); setImportResult(null); setShowImport(true); }} className="bg-[#12151C] border border-[#b56b38]/40 text-[#b56b38] hover:bg-[#b56b38]/10 rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2" data-testid="import-csv-btn">
            <Upload className="w-4 h-4" /> Import CSV
          </button>
          <button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2" data-testid="add-product-btn">
            <Plus className="w-4 h-4" /> Add Product
          </button>
        </div>
      </div>

      {/* Floating Selected Banner */}
      {selectedIds.size > 0 && (
        <div className="bg-[#cc3737]/10 border border-[#cc3737]/35 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4 animate-in fade-in slide-in-from-top-4 duration-300">
          <span className="text-white text-sm font-semibold">{selectedIds.size} product{selectedIds.size === 1 ? '' : 's'} selected</span>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => handleBulkStatus(true)} className="bg-[#12151C] hover:bg-white/[0.05] text-[#10B981] border border-white/10 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors">Activate</button>
            <button onClick={() => handleBulkStatus(false)} className="bg-[#12151C] hover:bg-white/[0.05] text-[#EF4444] border border-white/10 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors">Deactivate</button>
            <button onClick={() => { setBulkPricePreviews([]); setShowBulkPrice(true); }} className="bg-[#12151C] hover:bg-white/[0.05] text-[#b56b38] border border-white/10 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors">Bulk Price Update</button>
            <button onClick={() => setShowBulkInventory(true)} className="bg-[#12151C] hover:bg-white/[0.05] text-[#3B82F6] border border-white/10 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors">Bulk Inventory</button>
            <button onClick={handleBulkDelete} className="bg-[#cc3737] hover:bg-[#d94444] text-white px-4 py-1.5 rounded-full text-xs font-semibold transition-colors">Delete Selected</button>
          </div>
        </div>
      )}

      {/* Search and Filters Bar */}
      <div className="flex flex-col xl:flex-row gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[280px]">
          <Search className="w-4 h-4 text-[#64748B] absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by product name, brand, SKU, or barcode..."
            className="w-full bg-[#12151C] border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm text-white focus:outline-none focus:border-[#b56b38]/40"
            data-testid="product-search-input"
          />
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <select
            value={categoryFilter}
            onChange={e => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
            className="bg-[#12151C] border border-white/10 rounded-full px-4 py-2 text-sm text-white focus:outline-none focus:border-[#b56b38]/40"
            data-testid="product-category-filter"
          >
            <option value="All">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <select
            value={stockFilter}
            onChange={e => { setStockFilter(e.target.value); setCurrentPage(1); }}
            className="bg-[#12151C] border border-white/10 rounded-full px-4 py-2 text-sm text-white focus:outline-none focus:border-[#b56b38]/40"
            data-testid="product-stock-filter"
          >
            <option value="All">All Stock Levels</option>
            <option value="in_stock">In Stock (&gt; 0)</option>
            <option value="low_stock">Low Stock (≤ 5)</option>
            <option value="out_of_stock">Out of Stock (≤ 0)</option>
          </select>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="bg-[#12151C] border border-white/10 rounded-full px-4 py-2 text-sm text-white focus:outline-none focus:border-[#b56b38]/40"
          >
            <option value="All">All Statuses</option>
            <option value="active">Active (Visible)</option>
            <option value="inactive">Inactive (Hidden)</option>
          </select>
          <select
            value={sortKey}
            onChange={e => { setSortKey(e.target.value); setCurrentPage(1); }}
            className="bg-[#12151C] border border-white/10 rounded-full px-4 py-2 text-sm text-white focus:outline-none focus:border-[#b56b38]/40"
          >
            <option value="newest">Sort: Newest</option>
            <option value="oldest">Sort: Oldest</option>
            <option value="name">Sort: Name (A-Z)</option>
            <option value="price">Sort: Price (Low-High)</option>
            <option value="stock">Sort: Stock (High-Low)</option>
            <option value="category">Sort: Category</option>
            <option value="popularity">Sort: Popularity</option>
            <option value="recently_updated">Sort: Recently Updated</option>
          </select>
        </div>
      </div>

      {/* Main Grid/Table */}
      <div className="bg-[#12151C] border border-white/10 rounded-xl overflow-hidden shadow-lg">
        {loading ? (
          <div className="p-20 text-center flex flex-col items-center justify-center">
            <div className="w-8 h-8 border-2 border-[#b56b38] border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-xs text-[#94A3B8]">Loading product records from server...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left" data-testid="admin-inventory-table">
                <thead>
                  <tr className="border-b border-white/5 bg-[#0B0E14]/40">
                    <th className="px-4 py-3.5 w-10">
                      <input
                        type="checkbox"
                        checked={products.length > 0 && products.every(p => selectedIds.has(p.id))}
                        onChange={e => handleSelectAll(e.target.checked)}
                        className="rounded accent-[#cc3737] cursor-pointer"
                      />
                    </th>
                    <th className="px-4 py-3.5 text-[#64748B] font-semibold text-xs uppercase">Product Details</th>
                    <th className="px-4 py-3.5 text-[#64748B] font-semibold text-xs uppercase">SKU / Barcode</th>
                    <th className="px-4 py-3.5 text-[#64748B] font-semibold text-xs uppercase">Category</th>
                    <th className="px-4 py-3.5 text-[#64748B] font-semibold text-xs uppercase">Price Structure</th>
                    <th className="px-4 py-3.5 text-[#64748B] font-semibold text-xs uppercase text-center">Stock</th>
                    <th className="px-4 py-3.5 text-[#64748B] font-semibold text-xs uppercase">Flags</th>
                    <th className="px-4 py-3.5 text-[#64748B] font-semibold text-xs uppercase text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {products.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="p-12 text-center text-xs text-[#64748B]">No products found matching active filter constraints.</td>
                    </tr>
                  ) : (
                    products.map((p, idx) => {
                      const isSelected = selectedIds.has(p.id);
                      return (
                        <tr key={p.id || idx} className={`hover:bg-white/[0.02] transition-colors ${isSelected ? 'bg-[#cc3737]/5' : ''}`}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectOne(p.id)}
                              className="rounded accent-[#cc3737] cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded bg-[#0B0E14] border border-white/5 overflow-hidden flex-shrink-0 flex items-center justify-center">
                                {p.images?.[0] ? (
                                  <img src={p.images[0]} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <Package className="w-4 h-4 text-[#64748B]" />
                                )}
                              </div>
                              <div className="min-w-0">
                                <p className="text-white font-medium truncate max-w-[200px]">{p.name}</p>
                                {p.brand && <p className="text-[11px] text-[#64748B] truncate max-w-[150px]">{p.brand}</p>}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs">
                            {p.sku ? <div className="text-white" title="SKU">{p.sku}</div> : <div className="text-[#64748B]">No SKU</div>}
                            {p.barcode && <div className="text-[#64748B] mt-0.5" title="Barcode">{p.barcode}</div>}
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-[#94A3B8]">{p.category}</span>
                            {p.subcategory && <span className="block text-[10px] text-[#64748B]">{p.subcategory}</span>}
                          </td>
                          <td className="px-4 py-3">
                            {p.sale_price ? (
                              <div>
                                <span className="text-[#b56b38] font-semibold">${p.sale_price}</span>
                                <span className="text-[#64748B] line-through text-xs ml-2">${p.price}</span>
                              </div>
                            ) : (
                              <span className="text-white font-semibold">${p.price}</span>
                            )}
                            {p.cost_price && (
                              <div className="text-[10px] text-[#64748B] mt-0.5">Cost: ${p.cost_price}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center font-semibold">
                            <span className={p.stock > 10 ? 'text-[#10B981]' : p.stock > 0 ? 'text-[#F59E0B]' : 'text-[#EF4444]'}>
                              {p.stock}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 flex-wrap">
                              {p.age_restricted && <Badge className="bg-[#cc3737]/20 text-[#cc3737] hover:bg-[#cc3737]/20 border border-[#cc3737]/30 text-[9px] px-1.5 py-0">21+</Badge>}
                              {p.featured && <Badge className="bg-[#b56b38]/20 text-[#b56b38] hover:bg-[#b56b38]/20 border border-[#b56b38]/30 text-[9px] px-1.5 py-0">FEAT</Badge>}
                              {!p.visible && <Badge className="bg-white/5 text-[#64748B] border border-white/5 text-[9px] px-1.5 py-0">HIDDEN</Badge>}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex gap-2.5 justify-end">
                              <button onClick={() => handleEdit(p)} className="text-[#94A3B8] hover:text-white transition-colors" title="Edit Product">
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDelete(p.id)} className="text-[#64748B] hover:text-[#EF4444] transition-colors" title="Delete Product">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="border-t border-white/5 px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#0B0E14]/10">
                <div className="text-xs text-[#94A3B8]">
                  Showing <span className="font-semibold text-white">{products.length}</span> of <span className="font-semibold text-white">{totalProducts}</span> products
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={itemsPerPage}
                    onChange={e => { setItemsPerPage(parseInt(e.target.value)); setCurrentPage(1); }}
                    className="bg-[#0B0E14] border border-white/10 rounded px-2 py-1 text-xs text-[#94A3B8] focus:outline-none"
                  >
                    <option value={25}>25 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                    <option value={250}>250 / page</option>
                  </select>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-2.5 py-1 text-xs text-[#94A3B8] hover:text-white bg-[#0B0E14] border border-white/10 rounded disabled:opacity-40"
                    >
                      Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-2.5 py-1 text-xs rounded border transition-all ${currentPage === pageNum ? 'bg-[#cc3737] border-[#cc3737] text-white font-semibold' : 'bg-[#0B0E14] border-white/10 text-[#94A3B8] hover:text-white'}`}
                      >
                        {pageNum}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-2.5 py-1 text-xs text-[#94A3B8] hover:text-white bg-[#0B0E14] border border-white/10 rounded disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bulk Pricing Modal */}
      {showBulkPrice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-6 max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl">
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h3 className="font-['Playfair_Display'] text-xl text-white">Bulk Price Adjustments ({selectedIds.size} selected)</h3>
              <button onClick={() => setShowBulkPrice(false)} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            
            <div className="flex-1 overflow-y-auto space-y-4 pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#64748B] mb-1 font-medium">TARGET FIELD</label>
                  <select
                    value={bulkPriceForm.target}
                    onChange={e => setBulkPriceForm({...bulkPriceForm, target: e.target.value})}
                    className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#b56b38]/40"
                  >
                    <option value="price">Regular Retail Price</option>
                    <option value="sale_price">Promo Sale Price</option>
                    <option value="cost_price">Supplier Cost Price</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-[#64748B] mb-1 font-medium">ADJUSTMENT MODE</label>
                  <select
                    value={bulkPriceForm.mode}
                    onChange={e => setBulkPriceForm({...bulkPriceForm, mode: e.target.value})}
                    className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#b56b38]/40"
                  >
                    <option value="percent">Percentage Adjustment (%)</option>
                    <option value="fixed">Fixed Price Adjustment ($)</option>
                    <option value="set">Set Price Override ($)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {bulkPriceForm.mode !== 'set' && (
                  <div>
                    <label className="block text-xs text-[#64748B] mb-1 font-medium">DIRECTION</label>
                    <select
                      value={bulkPriceForm.direction}
                      onChange={e => setBulkPriceForm({...bulkPriceForm, direction: e.target.value})}
                      className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none"
                    >
                      <option value="increase">Increase (+)</option>
                      <option value="decrease">Decrease (-)</option>
                    </select>
                  </div>
                )}
                <div className={bulkPriceForm.mode === 'set' ? 'col-span-2' : ''}>
                  <label className="block text-xs text-[#64748B] mb-1 font-medium">VALUE</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder={bulkPriceForm.mode === 'percent' ? 'e.g. 10' : 'e.g. 2.99'}
                    value={bulkPriceForm.value}
                    onChange={e => setForm({...form}) || setBulkPriceForm({...bulkPriceForm, value: e.target.value})}
                    className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-[#64748B] mb-1 font-medium">ROUNDING RULE</label>
                <select
                  value={bulkPriceForm.roundRule}
                  onChange={e => setBulkPriceForm({...bulkPriceForm, roundRule: e.target.value})}
                  className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none"
                >
                  <option value="none">No Rounding (Exact Float)</option>
                  <option value="nearest_dollar">Round to Nearest Dollar (e.g. $12.00)</option>
                  <option value="nearest_99">Round to .99 Ending (e.g. $11.99)</option>
                  <option value="nearest_95">Round to .95 Ending (e.g. $11.95)</option>
                </select>
              </div>

              <button
                onClick={fetchBulkPricePreview}
                disabled={bulkPriceLoading || !bulkPriceForm.value}
                className="w-full bg-[#12151C] hover:bg-white/5 border border-white/10 hover:border-[#b56b38]/40 text-white rounded-lg py-2.5 text-xs font-semibold flex items-center justify-center gap-2"
              >
                {bulkPriceLoading ? 'Calculating Preview...' : 'Generate Adjustment Preview'}
              </button>

              {bulkPricePreviews.length > 0 && (
                <div className="border border-white/10 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                  <table className="w-full text-[11px] text-left">
                    <thead className="bg-[#0B0E14]/60 border-b border-white/5 text-[#64748B] uppercase font-bold sticky top-0">
                      <tr>
                        <th className="px-3 py-2">Product Name</th>
                        <th className="px-3 py-2 text-right">Old Value</th>
                        <th className="px-3 py-2 text-right text-[#b56b38]">New Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {bulkPricePreviews.map((p, idx) => (
                        <tr key={idx} className="hover:bg-white/[0.01]">
                          <td className="px-3 py-1.5 text-white truncate max-w-[250px]">{p.name}</td>
                          <td className="px-3 py-1.5 text-right text-[#64748B]">${p.old_value}</td>
                          <td className="px-3 py-1.5 text-right font-semibold text-[#10B981]">${p.new_value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex gap-2 mt-4 pt-3 border-t border-white/5 flex-shrink-0">
              <button onClick={() => setShowBulkPrice(false)} className="flex-1 bg-[#1A1D27] border border-white/10 text-[#94A3B8] rounded-full py-2.5 text-xs font-medium">Cancel</button>
              <button
                onClick={handleBulkPriceApply}
                disabled={bulkPricePreviews.length === 0}
                className="flex-1 bg-[#cc3737] hover:bg-[#d94444] disabled:opacity-40 text-white rounded-full py-2.5 text-xs font-semibold"
              >
                Apply Updates (Commit to DB)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Inventory Modal */}
      {showBulkInventory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-6 max-w-md w-full shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-['Playfair_Display'] text-lg text-white">Adjust Inventory ({selectedIds.size} items)</h3>
              <button onClick={() => setShowBulkInventory(false)} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs text-[#64748B] mb-1 font-medium">ADJUSTMENT ACTION</label>
                <select
                  value={bulkInventoryForm.mode}
                  onChange={e => setBulkInventoryForm({...bulkInventoryForm, mode: e.target.value})}
                  className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                >
                  <option value="add">Add Stock (+)</option>
                  <option value="subtract">Subtract Stock (-)</option>
                  <option value="replace">Set Absolute Quantity (=)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs text-[#64748B] mb-1 font-medium">QUANTITY VALUE</label>
                <input
                  type="number"
                  placeholder="e.g. 24"
                  value={bulkInventoryForm.value}
                  onChange={e => setBulkInventoryForm({...bulkInventoryForm, value: e.target.value})}
                  className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                />
              </div>

              <div>
                <label className="block text-xs text-[#64748B] mb-1 font-medium">REASON FOR ADJUSTMENT</label>
                <input
                  type="text"
                  placeholder="e.g. Bulk CSV Import, Damaged stock write-off"
                  value={bulkInventoryForm.reason}
                  onChange={e => setBulkInventoryForm({...bulkInventoryForm, reason: e.target.value})}
                  className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button onClick={() => setShowBulkInventory(false)} className="flex-1 bg-[#1A1D27] border border-white/10 text-[#94A3B8] rounded-full py-2 text-xs font-medium">Cancel</button>
              <button onClick={handleBulkInventoryApply} className="flex-1 bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-2 text-xs font-semibold">Apply Adjustments</button>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in" onClick={() => !importing && setShowImport(false)} data-testid="import-csv-modal">
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-['Playfair_Display'] text-xl text-white flex items-center gap-2"><Upload className="w-5 h-5 text-[#b56b38]" /> Bulk Import Catalog</h3>
              <button onClick={() => !importing && setShowImport(false)} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[#94A3B8] text-sm mb-4">Upload a CSV containing your liquor store inventory. Conflicting SKUs or barcodes will be updated automatically; unique ones will be created.</p>
            <div className="bg-[#0B0E14] border border-white/10 rounded-lg p-3 mb-4">
              <p className="text-[#64748B] text-xs font-medium mb-1">REQUIRED COLUMNS</p>
              <p className="text-white text-xs font-mono mb-2"><span className="text-[#EF4444]">name*</span>, <span className="text-[#EF4444]">category*</span>, <span className="text-[#EF4444]">price*</span>, sku, barcode, stock</p>
              <p className="text-[#64748B] text-xs font-medium mb-1">OPTIONAL COLUMNS</p>
              <p className="text-[#94A3B8] text-xs font-mono">brand, subcategory, sale_price, cost_price, abv, proof, sizes, country, region, seo_title, seo_description, images, description, visible</p>
            </div>
            <button onClick={downloadSampleCsv} className="w-full mb-3 bg-[#0B0E14] border border-white/10 hover:border-[#b56b38]/40 text-[#94A3B8] hover:text-white rounded-lg px-4 py-2.5 text-xs flex items-center justify-center gap-2 transition-colors" data-testid="download-sample-csv-btn">
              <FileText className="w-4 h-4" /> Download Sample Template CSV
            </button>
            <label className="flex items-center gap-3 bg-[#0B0E14] border border-dashed border-white/20 hover:border-[#b56b38]/40 rounded-lg px-4 py-4 cursor-pointer transition-colors mb-4">
              <Upload className="w-5 h-5 text-[#b56b38] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{importFile ? importFile.name : 'Choose CSV file...'}</p>
                {importFile && <p className="text-[#64748B] text-xs">{(importFile.size / 1024).toFixed(1)} KB</p>}
              </div>
              <input type="file" accept=".csv,text/csv" onChange={e => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }} className="hidden" data-testid="import-csv-input" />
            </label>
            {importResult && (
              <div className="bg-[#0B0E14] border border-white/10 rounded-lg p-4 mb-4 space-y-3" data-testid="import-result animate-in zoom-in-95">
                <div className="grid grid-cols-3 gap-2 text-center border-b border-white/5 pb-3">
                  <div><p className="text-2xl font-semibold text-[#10B981]">{importResult.created}</p><p className="text-[9px] text-[#64748B] uppercase">Created</p></div>
                  <div><p className="text-2xl font-semibold text-[#b56b38]">{importResult.updated}</p><p className="text-[9px] text-[#64748B] uppercase">Updated</p></div>
                  <div><p className="text-2xl font-semibold text-[#EF4444]">{importResult.skipped}</p><p className="text-[9px] text-[#64748B] uppercase">Skipped</p></div>
                </div>
                
                {importResult.errors_count > 0 && (
                  <div>
                    <p className="text-[#EF4444] text-xs font-semibold mb-1">{importResult.errors_count} critical error{importResult.errors_count === 1 ? '' : 's'} reported:</p>
                    <div className="max-h-24 overflow-y-auto space-y-1 bg-[#cc3737]/5 border border-[#cc3737]/10 p-2 rounded">
                      {importResult.errors.map((e, idx) => <p key={idx} className="text-[#EF4444] text-[10px] font-mono">Line {e.line}: {e.error}</p>)}
                    </div>
                  </div>
                )}
                {importResult.warnings_count > 0 && (
                  <div>
                    <p className="text-[#F59E0B] text-xs font-semibold mb-1">{importResult.warnings_count} validation warning{importResult.warnings_count === 1 ? '' : 's'}:</p>
                    <div className="max-h-24 overflow-y-auto space-y-1 bg-[#F59E0B]/5 border border-[#F59E0B]/10 p-2 rounded">
                      {importResult.warnings.map((w, idx) => <p key={idx} className="text-[#F59E0B] text-[10px] font-mono">Line {w.line}: {w.warning}</p>)}
                    </div>
                  </div>
                )}
                
                <button
                  onClick={() => downloadImportReport(importResult.import_id)}
                  className="w-full text-[#94A3B8] hover:text-white border border-white/5 hover:border-white/20 bg-white/5 rounded-lg py-2 text-xs font-medium flex items-center justify-center gap-1.5"
                >
                  <FileText className="w-3.5 h-3.5 text-[#b56b38]" /> Download Detailed Validation Report (.JSON)
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setShowImport(false)} disabled={importing} className="flex-1 border border-white/10 text-[#94A3B8] rounded-full py-2.5 text-xs font-medium" data-testid="import-csv-cancel">Close</button>
              <button onClick={submitImport} disabled={importing || !importFile} className="flex-1 bg-[#cc3737] hover:bg-[#d94444] disabled:opacity-40 text-white rounded-full py-2.5 text-xs font-semibold" data-testid="import-csv-submit">{importing ? 'Importing...' : 'Upload & Start Import'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Expanded Product Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in">
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-6 max-w-2xl w-full max-h-[92vh] flex flex-col shadow-2xl">
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h3 className="font-['Playfair_Display'] text-xl text-white">{editProduct ? 'Edit Product Parameters' : 'Create New Inventory Item'}</h3>
              <button onClick={() => setShowForm(false)} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-4 pr-1 scrollbar-thin">
              {/* Basic Details */}
              <div className="space-y-3">
                <h4 className="text-[11px] text-[#b56b38] font-bold uppercase tracking-wider">Basic Store Information</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="text-[10px] text-[#64748B] font-bold">PRODUCT NAME *</label>
                    <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Macallan Double Cask 12 Year Old" required className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white placeholder:text-[#64748B]" data-testid="product-name-input" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">BRAND / PRODUCER</label>
                    <input value={form.brand} onChange={e => setForm({...form, brand: e.target.value})} placeholder="e.g. Macallan" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">CATEGORY *</label>
                    <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white">
                      {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">SUBCATEGORY (optional)</label>
                    <input value={form.subcategory} onChange={e => setForm({...form, subcategory: e.target.value})} placeholder="e.g. Single Malt Scotch" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">SKU NUMBER</label>
                    <input value={form.sku} onChange={e => setForm({...form, sku: e.target.value})} placeholder="e.g. MAC-12-750" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white" />
                  </div>
                  <div className="col-span-2">
                    <label className="text-[10px] text-[#64748B] font-bold">BARCODE (UPC/EAN)</label>
                    <input value={form.barcode} onChange={e => setForm({...form, barcode: e.target.value})} placeholder="e.g. 085000021319" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white font-mono" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-[#64748B] font-bold">PRODUCT DESCRIPTION</label>
                  <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Write a short description..." rows={2} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white resize-none" />
                </div>
              </div>

              {/* Pricing & Stock */}
              <div className="space-y-3 pt-2 border-t border-white/5">
                <h4 className="text-[11px] text-[#b56b38] font-bold uppercase tracking-wider">Pricing &amp; Inventory Levels</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">REGULAR PRICE ($) *</label>
                    <input value={form.price} onChange={e => setForm({...form, price: e.target.value})} placeholder="0.00" required type="number" step="0.01" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white" data-testid="product-price-input" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">SALE PRICE ($)</label>
                    <input value={form.sale_price} onChange={e => setForm({...form, sale_price: e.target.value})} placeholder="0.00" type="number" step="0.01" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">COST PRICE ($)</label>
                    <input value={form.cost_price} onChange={e => setForm({...form, cost_price: e.target.value})} placeholder="0.00" type="number" step="0.01" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">STOCK COUNT *</label>
                    <input value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} placeholder="0" required type="number" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white" data-testid="product-stock-input" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">ABV (%)</label>
                    <input value={form.abv} onChange={e => setForm({...form, abv: e.target.value})} placeholder="e.g. 40.0" type="number" step="0.1" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">PROOF</label>
                    <input value={form.proof} onChange={e => setForm({...form, proof: e.target.value})} placeholder="e.g. 80" type="number" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white" />
                  </div>
                </div>
              </div>

              {/* Specifications & Origin */}
              <div className="space-y-3 pt-2 border-t border-white/5">
                <h4 className="text-[11px] text-[#b56b38] font-bold uppercase tracking-wider">Specifications &amp; Origin</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">ORIGIN COUNTRY</label>
                    <input value={form.country} onChange={e => setForm({...form, country: e.target.value})} placeholder="e.g. Scotland" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">ORIGIN REGION</label>
                    <input value={form.region} onChange={e => setForm({...form, region: e.target.value})} placeholder="e.g. Speyside" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white" />
                  </div>
                </div>
              </div>

              {/* SEO Parameters */}
              <div className="space-y-3 pt-2 border-t border-white/5">
                <h4 className="text-[11px] text-[#b56b38] font-bold uppercase tracking-wider">SEO Parameters</h4>
                <div className="space-y-2">
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">SEO TITLE</label>
                    <input value={form.seo_title} onChange={e => setForm({...form, seo_title: e.target.value})} placeholder="Meta title for Google results..." className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#64748B] font-bold">SEO DESCRIPTION</label>
                    <textarea value={form.seo_description} onChange={e => setForm({...form, seo_description: e.target.value})} placeholder="Meta description context..." rows={2} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-xs text-white resize-none" />
                  </div>
                </div>
              </div>

              {/* Images */}
              <div className="space-y-3 pt-2 border-t border-white/5">
                <h4 className="text-[11px] text-[#b56b38] font-bold uppercase tracking-wider flex items-center justify-between">
                  Product Image URLs
                  <button type="button" onClick={() => setForm({...form, images: [...form.images, '']})} className="text-xs text-[#cc3737] hover:text-[#d94444]">+ Add Image URL</button>
                </h4>
                {form.images.map((img, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <ImageUpload value={img} onChange={url => { const ni = [...form.images]; ni[idx] = url; setForm({...form, images: ni}); }} label={idx === 0 ? "Principal image url" : `Additional image URL #${idx + 1}`} />
                    {idx > 0 && (
                      <button type="button" onClick={() => { const ni = form.images.filter((_, i) => i !== idx); setForm({...form, images: ni}); }} className="text-[#64748B] hover:text-[#EF4444] mt-6"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
              </div>

              {/* Flags */}
              <div className="flex gap-6 pt-2 border-t border-white/5">
                <label className="flex items-center gap-2 text-xs text-[#94A3B8] cursor-pointer"><input type="checkbox" checked={form.age_restricted} onChange={e => setForm({...form, age_restricted: e.target.checked})} className="accent-[#cc3737]" /> 21+ Only (Age restricted)</label>
                <label className="flex items-center gap-2 text-xs text-[#94A3B8] cursor-pointer"><input type="checkbox" checked={form.featured} onChange={e => setForm({...form, featured: e.target.checked})} className="accent-[#cc3737]" /> Featured Item</label>
                <label className="flex items-center gap-2 text-xs text-[#94A3B8] cursor-pointer"><input type="checkbox" checked={form.visible} onChange={e => setForm({...form, visible: e.target.checked})} className="accent-[#cc3737]" /> Visible to customers</label>
              </div>

              {/* Variants / Sub-items */}
              <div className="border border-white/10 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[#94A3B8] text-xs font-semibold uppercase tracking-wider">Sub-Items / Size Variants</span>
                  <button type="button" onClick={() => setForm({...form, variants: [...form.variants, {name: '', price: '', sku: '', barcode: '', description: ''}]})} className="text-[#b56b38] hover:text-white text-xs font-semibold flex items-center gap-1" data-testid="add-variant-btn"><Plus className="w-3.5 h-3.5" /> Add Size Option</button>
                </div>
                {form.variants.length === 0 && <p className="text-[#64748B] text-xs">This product has no size configurations. Individual item specs will be used.</p>}
                
                <div className="space-y-3.5">
                  {form.variants.map((v, vi) => (
                    <div key={vi} className="bg-[#0B0E14]/40 border border-white/5 rounded-lg p-3 relative space-y-2">
                      <button type="button" onClick={() => { const nv = form.variants.filter((_, i) => i !== vi); setForm({...form, variants: nv}); }} className="text-[#64748B] hover:text-[#EF4444] absolute right-2.5 top-2.5"><Trash2 className="w-3.5 h-3.5" /></button>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[9px] text-[#64748B] font-bold">VARIANT NAME (e.g. 750ml, 1.75L)</label>
                          <input value={v.name} onChange={e => { const nv = [...form.variants]; nv[vi] = {...nv[vi], name: e.target.value}; setForm({...form, variants: nv}); }} placeholder="Variant size/option" required className="w-full bg-[#0B0E14] border border-white/10 rounded px-2 py-1 text-xs text-white" />
                        </div>
                        <div>
                          <label className="text-[9px] text-[#64748B] font-bold">PRICE ($)</label>
                          <input value={v.price} onChange={e => { const nv = [...form.variants]; nv[vi] = {...nv[vi], price: e.target.value}; setForm({...form, variants: nv}); }} placeholder="Price value" required type="number" step="0.01" className="w-full bg-[#0B0E14] border border-white/10 rounded px-2 py-1 text-xs text-white" />
                        </div>
                        <div>
                          <label className="text-[9px] text-[#64748B] font-bold">SKU</label>
                          <input value={v.sku} onChange={e => { const nv = [...form.variants]; nv[vi] = {...nv[vi], sku: e.target.value}; setForm({...form, variants: nv}); }} placeholder="Variant specific SKU" className="w-full bg-[#0B0E14] border border-white/10 rounded px-2 py-1 text-xs text-white font-mono" />
                        </div>
                        <div>
                          <label className="text-[9px] text-[#64748B] font-bold">BARCODE</label>
                          <input value={v.barcode} onChange={e => { const nv = [...form.variants]; nv[vi] = {...nv[vi], barcode: e.target.value}; setForm({...form, variants: nv}); }} placeholder="Variant specific Barcode" className="w-full bg-[#0B0E14] border border-white/10 rounded px-2 py-1 text-xs text-white font-mono" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t border-white/5 flex-shrink-0">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 bg-[#1A1D27] border border-white/10 text-[#94A3B8] rounded-full py-2.5 text-xs font-semibold">Discard Changes</button>
                <button type="submit" className="flex-1 bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-2.5 text-xs font-semibold" data-testid="save-product-btn">{editProduct ? 'Update Product' : 'Create Product'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ImageUpload({ value, onChange, label = "Image" }) {
  const [uploading, setUploading] = useState(false);
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(res.data.url);
      toast.success('Image uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };
  return (
    <div>
      <label className="text-[#64748B] text-xs mb-1 block">{label}</label>
      <div className="flex gap-2 items-center">
        {value && <img src={value} alt="" className="w-10 h-10 rounded object-cover bg-[#1A1D27]" />}
        <label className="flex-1 flex items-center gap-2 bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-[#94A3B8] cursor-pointer hover:border-white/20 transition-colors">
          <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload Image'}
          <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" disabled={uploading} />
        </label>
      </div>
      <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder="Or paste image URL" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-1.5 text-xs text-white mt-1 placeholder:text-[#64748B]" />
    </div>
  );
}

function CustomersTab() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => { fetchCustomers(); }, []);
  const fetchCustomers = async () => {
    try { const res = await api.get('/admin/customers'); setCustomers(res.data.customers); } catch {} finally { setLoading(false); }
  };

  const viewCustomer = async (id) => {
    try {
      const res = await api.get(`/admin/customers/${id}`);
      setSelectedCustomer(res.data);
      setEditForm({ name: res.data.name || '', phone: res.data.phone || '', email: res.data.email || '', loyalty_points: res.data.loyalty_points || 0, tier: res.data.tier || 'bronze' });
    } catch { toast.error('Failed to load customer'); }
  };

  const saveCustomer = async () => {
    if (!selectedCustomer) return;
    try {
      await api.put(`/admin/customers/${selectedCustomer.id}`, editForm);
      toast.success('Customer updated');
      fetchCustomers();
      setSelectedCustomer(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const filtered = searchTerm ? customers.filter(c => (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (c.email || '').toLowerCase().includes(searchTerm.toLowerCase())) : customers;

  return (
    <div data-testid="admin-customers">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Playfair_Display'] text-2xl text-white">Customers ({customers.length})</h2>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search customers..." className="w-full bg-[#12151C] border border-white/10 rounded-md pl-9 pr-3 py-1.5 text-sm text-white" data-testid="customer-search" />
        </div>
      </div>

      <div className="bg-[#12151C] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead><tr className="border-b border-white/5">
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Name</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Email</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Phone</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Orders</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Loyalty</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Tier</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-white">{c.name || 'N/A'}</td>
                  <td className="px-4 py-3 text-[#94A3B8]">{c.email}</td>
                  <td className="px-4 py-3 text-[#94A3B8]">{c.phone || '-'}</td>
                  <td className="px-4 py-3 text-white">{c.order_count || 0}</td>
                  <td className="px-4 py-3 text-[#b56b38]">{c.loyalty_points || 0} pts</td>
                  <td className="px-4 py-3 text-[#94A3B8] capitalize">{c.tier || 'bronze'}</td>
                  <td className="px-4 py-3"><button onClick={() => viewCustomer(c.id)} className="text-[#94A3B8] hover:text-white flex items-center gap-1 text-xs" data-testid={`view-customer-${i}`}><Eye className="w-3.5 h-3.5" /> View</button></td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#64748B]">No customers found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customer Detail/Edit Modal */}
      {selectedCustomer && editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setSelectedCustomer(null)}>
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-['Playfair_Display'] text-xl text-white">Customer Details</h3>
              <button onClick={() => setSelectedCustomer(null)} className="text-[#64748B] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div><label className="text-[#64748B] text-xs mb-1 block">Name</label><input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="edit-customer-name" /></div>
              <div><label className="text-[#64748B] text-xs mb-1 block">Email</label><input value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" /></div>
              <div><label className="text-[#64748B] text-xs mb-1 block">Phone</label><input value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[#64748B] text-xs mb-1 block">Loyalty Points</label><input value={editForm.loyalty_points} onChange={e => setEditForm({...editForm, loyalty_points: parseInt(e.target.value) || 0})} type="number" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" /></div>
                <div><label className="text-[#64748B] text-xs mb-1 block">Tier</label><select value={editForm.tier} onChange={e => setEditForm({...editForm, tier: e.target.value})} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white"><option value="bronze">Bronze</option><option value="silver">Silver</option><option value="gold">Gold</option><option value="platinum">Platinum</option></select></div>
              </div>
              {/* Recent Orders */}
              {selectedCustomer.recent_orders?.length > 0 && (
                <div className="border-t border-white/5 pt-3 mt-3">
                  <p className="text-[#94A3B8] text-sm font-medium mb-2">Recent Orders</p>
                  {selectedCustomer.recent_orders.map((o, i) => (
                    <div key={i} className="flex justify-between py-1.5 text-xs border-b border-white/5">
                      <span className="text-[#b56b38]">{o.order_number}</span>
                      <span className="text-[#94A3B8]">{o.items?.length} items</span>
                      <span className="text-white">${o.total?.toFixed(2)}</span>
                      <span className={`${o.status === 'delivered' ? 'text-[#10B981]' : 'text-[#94A3B8]'}`}>{o.status}</span>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={saveCustomer} className="w-full bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-2.5 font-medium mt-2" data-testid="save-customer-btn">Save Changes</button>
              {/* Ban / Unban */}
              <div className="border-t border-white/5 pt-3 mt-3">
                {selectedCustomer.banned ? (
                  <button onClick={async () => { try { await api.put(`/admin/customers/${selectedCustomer.id}/ban`, {banned: false}); toast.success('Customer unbanned'); fetchCustomers(); setSelectedCustomer(null); } catch { toast.error('Failed'); }}} className="w-full border border-[#10B981]/30 text-[#10B981] rounded-full py-2 text-sm font-medium hover:bg-[#10B981]/10" data-testid="unban-customer-btn">Unban Customer</button>
                ) : (
                  <div>
                    <input id="ban-reason" placeholder="Ban reason (fraud, abuse, etc.)" className="w-full bg-[#0B0E14] border border-[#EF4444]/20 rounded-md px-3 py-2 text-sm text-white mb-2" data-testid="ban-reason-input" />
                    <button onClick={async () => { const reason = document.getElementById('ban-reason').value; try { await api.put(`/admin/customers/${selectedCustomer.id}/ban`, {banned: true, reason}); toast.success('Customer banned'); fetchCustomers(); setSelectedCustomer(null); } catch { toast.error('Failed'); }}} className="w-full bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-full py-2 text-sm font-medium" data-testid="ban-customer-btn"><Ban className="w-3.5 h-3.5 inline mr-1" /> Ban Customer</button>
                  </div>
                )}
              </div>
              {/* Admin Notes */}
              <div className="border-t border-white/5 pt-3 mt-3">
                <p className="text-[#94A3B8] text-sm font-medium mb-2 flex items-center gap-1"><StickyNote className="w-3.5 h-3.5" /> Internal Notes</p>
                {selectedCustomer.admin_notes?.map((note, ni) => (
                  <div key={ni} className="bg-[#0B0E14] rounded px-3 py-2 mb-1 text-xs"><span className="text-[#b56b38]">{note.by}</span><span className="text-[#64748B]"> · {new Date(note.at).toLocaleDateString()}</span><p className="text-[#94A3B8] mt-0.5">{note.text}</p></div>
                ))}
                <div className="flex gap-2 mt-1">
                  <input id="customer-note" placeholder="Add internal note..." className="flex-1 bg-[#0B0E14] border border-white/10 rounded-md px-3 py-1.5 text-xs text-white" data-testid="customer-note-input" />
                  <button onClick={async () => { const note = document.getElementById('customer-note').value; if (!note) return; try { await api.post(`/admin/customers/${selectedCustomer.id}/notes`, {note}); toast.success('Note added'); viewCustomer(selectedCustomer.id); document.getElementById('customer-note').value=''; } catch { toast.error('Failed'); }}} className="bg-[#b56b38] text-white rounded-md px-3 py-1.5 text-xs" data-testid="add-note-btn">Add</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DriversTab() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editDriver, setEditDriver] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', vehicle_info: '', status: 'active' });

  useEffect(() => { fetchDrivers(); }, []);
  const fetchDrivers = async () => {
    try { const res = await api.get('/admin/drivers'); setDrivers(res.data.drivers); } catch {} finally { setLoading(false); }
  };

  const handleEditClick = (d) => {
    setEditDriver(d);
    setForm({
      name: d.name || '',
      email: d.email || '',
      password: '',
      phone: d.phone || '',
      vehicle_info: d.vehicle_info || '',
      status: d.status || 'active'
    });
    setShowForm(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete driver "${name}"?`)) return;
    try {
      await api.delete(`/admin/drivers/${id}`);
      toast.success('Driver deleted');
      fetchDrivers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete driver');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editDriver) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/admin/drivers/${editDriver.id}`, payload);
        toast.success('Driver updated');
      } else {
        await api.post('/admin/drivers', form);
        toast.success('Driver added');
      }
      setShowForm(false);
      setForm({ name: '', email: '', password: '', phone: '', vehicle_info: '', status: 'active' });
      setEditDriver(null);
      fetchDrivers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save driver');
    }
  };

  const handleClose = () => {
    setShowForm(false);
    setForm({ name: '', email: '', password: '', phone: '', vehicle_info: '', status: 'active' });
    setEditDriver(null);
  };

  const filteredDrivers = drivers.filter(d => 
    (d.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.phone || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="admin-drivers">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <h2 className="font-['Playfair_Display'] text-2xl text-white">Drivers ({filteredDrivers.length})</h2>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
            <input 
              value={searchTerm} 
              onChange={e => setSearchTerm(e.target.value)} 
              placeholder="Search drivers..." 
              className="w-full bg-[#12151C] border border-white/10 rounded-md pl-9 pr-3 py-1.5 text-sm text-white" 
              data-testid="driver-search-input" 
            />
          </div>
        </div>
        <button onClick={() => { setEditDriver(null); setShowForm(true); }} className="bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2 self-start sm:self-auto" data-testid="add-driver-btn"><Plus className="w-4 h-4" /> Add Driver</button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={handleClose}>
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between mb-4"><h3 className="font-['Playfair_Display'] text-xl text-white">{editDriver ? 'Edit Driver' : 'Add Driver'}</h3><button onClick={handleClose} className="text-[#64748B]"><X className="w-5 h-5" /></button></div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Full Name *</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Full Name" required className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="driver-name-input" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Email *</label>
                <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Email" required type="email" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="driver-email-input" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Password {editDriver ? '(leave blank to keep unchanged)' : '*'}</label>
                <input value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder={editDriver ? "••••••••" : "Password"} required={!editDriver} type="password" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="driver-password-input" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Phone</label>
                <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Phone" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Vehicle Info</label>
                <input value={form.vehicle_info} onChange={e => setForm({...form, vehicle_info: e.target.value})} placeholder="Vehicle Info" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Status</label>
                <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <button type="submit" className="w-full bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-2.5 font-medium mt-4" data-testid="save-driver-btn">{editDriver ? 'Save Changes' : 'Add Driver'}</button>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#cc3737] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDrivers.map((d, i) => (
            <div key={d.id || i} className="bg-[#12151C] border border-white/10 rounded-xl p-5 flex flex-col justify-between" data-testid={`driver-card-${i}`}>
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-medium">{d.name}</h3>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase ${d.status === 'inactive' ? 'bg-[#EF4444]/20 text-[#EF4444]' : 'bg-[#10B981]/20 text-[#10B981]'}`}>
                      {d.status || 'active'}
                    </span>
                    <span className={`w-2.5 h-2.5 rounded-full ${d.is_online ? 'bg-[#10B981]' : 'bg-[#64748B]'}`} title={d.is_online ? 'Online (on shift)' : 'Offline'} />
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  <p className="text-[#94A3B8]">{d.email}</p>
                  <p className="text-[#64748B]">{d.phone || 'No phone'}</p>
                  {d.vehicle_info && <p className="text-[#64748B]">{d.vehicle_info}</p>}
                  {d.created_at && (
                    <p className="text-[#64748B] text-xs pt-1">
                      Joined: {new Date(d.created_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between">
                <div className="flex gap-4 text-xs">
                  <span className="text-[#b56b38]">{d.total_deliveries || 0} deliveries</span>
                  <span className="text-[#94A3B8]">Rating: {d.rating || '5.0'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleEditClick(d)} className="p-1.5 bg-[#1A1D27] border border-white/5 rounded-md text-[#94A3B8] hover:text-white hover:border-white/20 transition-all" title="Edit Driver"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleDelete(d.id, d.name)} className="p-1.5 bg-[#1A1D27] border border-white/5 rounded-md text-[#EF4444] hover:bg-[#EF4444]/10 hover:border-[#EF4444]/30 transition-all" title="Delete Driver"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
          {filteredDrivers.length === 0 && <p className="text-[#64748B] col-span-3 text-center py-8">No drivers found</p>}
        </div>
      )}
    </div>
  );
}

function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editStaff, setEditStaff] = useState(null);
  const [showResetModal, setShowResetModal] = useState(null);
  const [tempPasswordInput, setTempPasswordInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    staff_role: 'Order Manager',
    status: 'active',
    permissions: {
      orders: ['view'],
      customers: ['view'],
      products: ['view'],
      inventory: ['view'],
      drivers: ['view'],
      reports: ['view']
    }
  });

  const availablePermissions = [
    { category: 'orders', label: 'Orders', actions: ['view', 'edit'] },
    { category: 'customers', label: 'Customers', actions: ['view'] },
    { category: 'products', label: 'Products', actions: ['view', 'create', 'edit'] },
    { category: 'inventory', label: 'Inventory', actions: ['view', 'edit'] },
    { category: 'drivers', label: 'Drivers', actions: ['view', 'create', 'edit', 'delete'] },
    { category: 'reports', label: 'Reports', actions: ['view'] }
  ];

  useEffect(() => { fetchStaff(); }, []);
  const fetchStaff = async () => {
    try {
      const res = await api.get('/admin/staff');
      setStaff(res.data.staff || []);
    } catch {} finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (category, action, checked) => {
    setForm(prev => {
      const current = prev.permissions[category] || [];
      const updated = checked 
        ? [...current, action]
        : current.filter(a => a !== action);
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [category]: updated
        }
      };
    });
  };

  const handleEditClick = (s) => {
    setEditStaff(s);
    setForm({
      first_name: s.first_name || s.name?.split(' ')[0] || '',
      last_name: s.last_name || s.name?.split(' ').slice(1).join(' ') || '',
      email: s.email || '',
      phone: s.phone || '',
      password: '',
      staff_role: s.staff_role || 'Order Manager',
      status: s.status || 'active',
      permissions: s.permissions || {}
    });
    setShowForm(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete staff member "${name}"?`)) return;
    try {
      await api.delete(`/admin/staff/${id}`);
      toast.success('Staff member deleted');
      fetchStaff();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete staff member');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!tempPasswordInput.trim()) return toast.error('Please enter a password');
    try {
      await api.put(`/admin/staff/${showResetModal.id}/reset-password`, { temp_password: tempPasswordInput });
      toast.success(`Password reset for ${showResetModal.name}`);
      setShowResetModal(null);
      setTempPasswordInput('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset password');
    }
  };

  const generateRandomPassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let pwd = 'Staff-';
    for (let i = 0; i < 8; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setTempPasswordInput(pwd);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editStaff) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/admin/staff/${editStaff.id}`, payload);
        toast.success('Staff member updated');
      } else {
        await api.post('/admin/staff', form);
        toast.success('Staff member created');
      }
      setShowForm(false);
      resetForm();
      fetchStaff();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save staff member');
    }
  };

  const resetForm = () => {
    setForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      staff_role: 'Order Manager',
      status: 'active',
      permissions: {
        orders: ['view'],
        customers: ['view'],
        products: ['view'],
        inventory: ['view'],
        drivers: ['view'],
        reports: ['view']
      }
    });
    setEditStaff(null);
  };

  const filteredStaff = staff.filter(s => {
    const matchesSearch = 
      (s.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.phone || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !roleFilter || s.staff_role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <div data-testid="admin-staff">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-['Playfair_Display'] text-2xl text-white">Staff Management ({filteredStaff.length})</h2>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
            <input 
              value={searchTerm} 
              onChange={e => setSearchTerm(e.target.value)} 
              placeholder="Search staff..." 
              className="w-full bg-[#12151C] border border-white/10 rounded-md pl-9 pr-3 py-1.5 text-sm text-white" 
              data-testid="staff-search-input"
            />
          </div>
          <select 
            value={roleFilter} 
            onChange={e => setRoleFilter(e.target.value)} 
            className="bg-[#12151C] border border-white/10 rounded-md px-3 py-1.5 text-sm text-white"
            data-testid="staff-role-filter"
          >
            <option value="">All Roles</option>
            <option value="Manager">Manager</option>
            <option value="Customer Support">Customer Support</option>
            <option value="Order Manager">Order Manager</option>
            <option value="Inventory Staff">Inventory Staff</option>
          </select>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2 self-start md:self-auto" data-testid="add-staff-btn"><Plus className="w-4 h-4" /> Add Staff</button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => { setShowForm(false); resetForm(); }}>
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-6 max-w-lg w-full my-8" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between mb-4"><h3 className="font-['Playfair_Display'] text-xl text-white">{editStaff ? 'Edit Staff Member' : 'Add Staff Member'}</h3><button onClick={() => { setShowForm(false); resetForm(); }} className="text-[#64748B]"><X className="w-5 h-5" /></button></div>
            <form onSubmit={handleSubmit} className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">First Name *</label>
                  <input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} placeholder="First Name" required className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="staff-first-name" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">Last Name *</label>
                  <input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} placeholder="Last Name" required className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="staff-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">Email *</label>
                  <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="Email" required type="email" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="staff-email" />
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">Phone</label>
                  <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="Phone" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Password {editStaff ? '(leave blank to keep unchanged)' : '*'}</label>
                <input value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder={editStaff ? "••••••••" : "Password"} required={!editStaff} type="password" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="staff-password" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">Role *</label>
                  <select value={form.staff_role} onChange={e => setForm({...form, staff_role: e.target.value})} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white">
                    <option value="Manager">Manager</option>
                    <option value="Customer Support">Customer Support</option>
                    <option value="Order Manager">Order Manager</option>
                    <option value="Inventory Staff">Inventory Staff</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#94A3B8] mb-1 block">Status</label>
                  <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Permissions Checklist */}
              <div className="border-t border-white/10 pt-3 mt-2">
                <label className="text-sm font-medium text-white mb-2 block">Permissions</label>
                <div className="space-y-3">
                  {availablePermissions.map(p => (
                    <div key={p.category} className="bg-[#0B0E14] p-3 rounded-lg border border-white/5">
                      <span className="text-xs font-bold text-[#b56b38] uppercase tracking-wider">{p.label}</span>
                      <div className="flex gap-4 mt-1.5">
                        {p.actions.map(act => {
                          const isChecked = (form.permissions[p.category] || []).includes(act);
                          return (
                            <label key={act} className="flex items-center gap-2 text-sm text-[#94A3B8] cursor-pointer hover:text-white">
                              <input 
                                type="checkbox" 
                                checked={isChecked} 
                                onChange={e => handleCheckboxChange(p.category, act, e.target.checked)}
                                className="accent-[#cc3737]"
                              />
                              <span className="capitalize">{act}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button type="submit" className="w-full bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-2.5 font-medium mt-4" data-testid="save-staff-btn">{editStaff ? 'Save Changes' : 'Create Staff Member'}</button>
            </form>
          </div>
        </div>
      )}

      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowResetModal(null)}>
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between mb-3"><h3 className="font-['Playfair_Display'] text-lg text-white">Reset Password</h3><button onClick={() => setShowResetModal(null)} className="text-[#64748B]"><X className="w-5 h-5" /></button></div>
            <p className="text-sm text-[#94A3B8] mb-4">Reset password for <strong>{showResetModal.name}</strong> ({showResetModal.email}).</p>
            <form onSubmit={handleResetPassword} className="space-y-3">
              <div className="flex gap-2">
                <input 
                  value={tempPasswordInput} 
                  onChange={e => setTempPasswordInput(e.target.value)} 
                  placeholder="New password" 
                  required 
                  className="flex-1 bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" 
                  data-testid="temp-password-input"
                />
                <button type="button" onClick={generateRandomPassword} className="bg-[#1A1D27] border border-white/10 text-white rounded-md px-3 text-xs hover:bg-white/5 transition-all">Generate</button>
              </div>
              <div className="flex gap-2 pt-2">
                <button type="submit" className="flex-1 bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-2 text-sm font-medium">Reset Password</button>
                <button type="button" onClick={() => setShowResetModal(null)} className="flex-1 border border-white/10 text-white rounded-full py-2 text-sm font-medium hover:bg-white/5">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#cc3737] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="bg-[#12151C] border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5 bg-[#0B0E14]/50">
                  <th className="text-left px-6 py-4 text-[#64748B] font-medium uppercase tracking-wider">Staff Member</th>
                  <th className="text-left px-6 py-4 text-[#64748B] font-medium uppercase tracking-wider">Contact Info</th>
                  <th className="text-left px-6 py-4 text-[#64748B] font-medium uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-4 text-[#64748B] font-medium uppercase tracking-wider">Joined Date</th>
                  <th className="text-left px-6 py-4 text-[#64748B] font-medium uppercase tracking-wider">Last Login</th>
                  <th className="text-left px-6 py-4 text-[#64748B] font-medium uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredStaff.map((s, idx) => (
                  <tr key={s.id || idx} className="hover:bg-white/[0.02]" data-testid={`staff-row-${idx}`}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-white">{s.name}</div>
                      <div className="text-xs text-[#b56b38]">{s.staff_role || 'Staff'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-white">{s.email}</div>
                      <div className="text-xs text-[#64748B]">{s.phone || 'No phone'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase ${s.status === 'inactive' ? 'bg-[#EF4444]/20 text-[#EF4444]' : 'bg-[#10B981]/20 text-[#10B981]'}`}>
                        {s.status || 'active'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[#94A3B8]">
                      {s.created_at ? new Date(s.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-[#94A3B8]">
                      {s.last_login ? new Date(s.last_login).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : 'Never'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleEditClick(s)} className="p-1.5 bg-[#1A1D27] border border-white/5 rounded-md text-[#94A3B8] hover:text-white hover:border-white/20 transition-all" title="Edit Staff" data-testid={`edit-staff-${idx}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setShowResetModal({ id: s.id, name: s.name, email: s.email })} className="p-1.5 bg-[#1A1D27] border border-white/5 rounded-md text-[#b56b38] hover:text-[#e6c65a] hover:border-white/20 transition-all" title="Reset Password" data-testid={`reset-pwd-staff-${idx}`}><Key className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleDelete(s.id, s.name)} className="p-1.5 bg-[#1A1D27] border border-white/5 rounded-md text-[#EF4444] hover:bg-[#EF4444]/10 hover:border-[#EF4444]/30 transition-all" title="Delete Staff" data-testid={`delete-staff-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredStaff.length === 0 && (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-[#64748B] text-center">No staff members found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);
  const fetchSettings = async () => {
    try { const res = await api.get('/settings'); setSettings(res.data.settings); } catch {} finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try { await api.put('/settings', { settings }); toast.success('Settings saved'); } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (!currentPwd || !newPwd) return toast.error('Both fields required');
    if (newPwd.length < 6) return toast.error('New password must be at least 6 characters');
    setPwdSaving(true);
    try {
      await api.put('/auth/change-password', { current_password: currentPwd, new_password: newPwd });
      toast.success('Password changed successfully');
      setCurrentPwd(''); setNewPwd('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to change password'); }
    finally { setPwdSaving(false); }
  };

  const updateSetting = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  if (loading) return <LoadingSkeleton />;

  return (
    <div data-testid="admin-settings">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Playfair_Display'] text-2xl text-white">Settings</h2>
        <button onClick={handleSave} disabled={saving} className="bg-[#cc3737] hover:bg-[#d94444] disabled:opacity-50 text-white rounded-full px-6 py-2 text-sm font-medium" data-testid="save-settings-btn">{saving ? 'Saving...' : 'Save Changes'}</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Store Info */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-medium mb-4">Store Information</h3>
          <div className="space-y-3">
            <div><label className="text-[#64748B] text-xs mb-1 block">Store Name</label><input value={settings.store_name || ''} onChange={e => updateSetting('store_name', e.target.value)} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block">Address</label><input value={settings.store_address || ''} onChange={e => updateSetting('store_address', e.target.value)} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block">Phone</label><input value={settings.store_phone || ''} onChange={e => updateSetting('store_phone', e.target.value)} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block">Email</label><input value={settings.store_email || ''} onChange={e => updateSetting('store_email', e.target.value)} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" /></div>
          </div>
        </div>

        {/* Store Controls */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-medium mb-4">Store Controls</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between"><span className="text-[#94A3B8] text-sm">Store Open</span><Switch checked={settings.store_open !== false} onCheckedChange={v => updateSetting('store_open', v)} data-testid="store-open-toggle" /></div>
            <div className="flex items-center justify-between"><span className="text-[#94A3B8] text-sm">Accepting Orders</span><Switch checked={settings.accepting_orders !== false} onCheckedChange={v => updateSetting('accepting_orders', v)} data-testid="accepting-orders-toggle" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block">Delivery Fee ($)</label><input value={settings.delivery_fee || ''} onChange={e => updateSetting('delivery_fee', parseFloat(e.target.value) || 0)} type="number" step="0.01" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="delivery-fee-input" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block">Min Order ($)</label><input value={settings.min_order || ''} onChange={e => updateSetting('min_order', parseFloat(e.target.value) || 0)} type="number" step="0.01" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" /></div>
            <p className="text-[#64748B] text-[11px] mt-2">Payment & pickup toggles now live in the <strong className="text-[#b56b38]">Payment Methods</strong> card below.</p>
          </div>
        </div>

        {/* Tax Settings */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5" data-testid="tax-settings">
          <h3 className="text-white font-medium mb-2 flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#b56b38]" /> Tax Settings</h3>
          <p className="text-[#64748B] text-xs mb-4">Control sales tax applied to every order. Customer will see the label and rate you set here.</p>
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3">
              <div>
                <p className="text-white text-sm font-medium">Collect Tax</p>
                <p className="text-[#64748B] text-xs">Turn OFF to charge customers no tax</p>
              </div>
              <Switch checked={settings.tax_enabled !== false} onCheckedChange={v => updateSetting('tax_enabled', v)} data-testid="tax-enabled-toggle" />
            </div>
            <div>
              <label className="text-[#64748B] text-xs mb-1 block">Tax Label (shown on receipts)</label>
              <input value={settings.tax_label || 'Sales Tax'} onChange={e => updateSetting('tax_label', e.target.value)} placeholder="e.g., Sales Tax, NJ Tax" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="tax-label-input" />
            </div>
            <div>
              <label className="text-[#64748B] text-xs mb-1 block">Tax Rate (decimal) — {((parseFloat(settings.tax_rate) || 0) * 100).toFixed(3)}%</label>
              <input value={settings.tax_rate ?? 0.06625} onChange={e => updateSetting('tax_rate', parseFloat(e.target.value) || 0)} type="number" step="0.00001" min="0" max="1" placeholder="0.06625" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono" data-testid="tax-rate-input" />
              <p className="text-[#64748B] text-[10px] mt-1">NJ default: 0.06625 (6.625%). Enter as a decimal (e.g., 0.08 = 8%).</p>
            </div>
          </div>
        </div>

        {/* SMTP / Email Notifications */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5 lg:col-span-2" data-testid="smtp-settings">
          <h3 className="text-white font-medium mb-2 flex items-center gap-2"><Mail className="w-4 h-4 text-[#b56b38]" /> Email Notifications (SMTP)</h3>
          <p className="text-[#64748B] text-xs mb-4">Configure outbound email for password-reset codes, order confirmations, status updates, and account notifications. Leave blank to log codes to the server console only.</p>
          <div className="flex items-center justify-between bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3 mb-4">
            <div>
              <p className="text-white text-sm font-medium">Email Sending Enabled</p>
              <p className="text-[#64748B] text-xs">Turn OFF to temporarily stop all outbound email</p>
            </div>
            <Switch checked={settings.smtp_enabled !== false} onCheckedChange={v => updateSetting('smtp_enabled', v)} data-testid="smtp-enabled-toggle" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><label className="text-[#64748B] text-xs mb-1 block">SMTP Host <span className="text-[#EF4444]">*</span></label><input value={settings.smtp_host || ''} onChange={e => updateSetting('smtp_host', e.target.value)} placeholder="smtp.gmail.com" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="smtp-host-input" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block">SMTP Port</label><input value={settings.smtp_port || 587} onChange={e => updateSetting('smtp_port', parseInt(e.target.value) || 587)} type="number" placeholder="587" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="smtp-port-input" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block">SMTP Username</label><input value={settings.smtp_user || ''} onChange={e => updateSetting('smtp_user', e.target.value)} placeholder="you@gmail.com" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="smtp-user-input" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block flex items-center justify-between"><span>SMTP Password / App Password</span>{settings.smtp_password === '__SECRET_SET__' && <span className="text-[10px] text-[#10B981]">✓ Saved · type to replace</span>}</label><input value={settings.smtp_password === '__SECRET_SET__' ? '' : (settings.smtp_password || '')} onChange={e => updateSetting('smtp_password', e.target.value)} type="password" placeholder={settings.smtp_password === '__SECRET_SET__' ? '••••••••••••  (leave blank to keep current)' : 'App-specific password'} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="smtp-password-input" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block">From Address <span className="text-[#EF4444]">*</span></label><input value={settings.smtp_from || ''} onChange={e => updateSetting('smtp_from', e.target.value)} placeholder="orders@hobokenlgd.com" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="smtp-from-input" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block">From Display Name</label><input value={settings.smtp_from_name || 'Hoboken Liquor Grocery & Deli'} onChange={e => updateSetting('smtp_from_name', e.target.value)} placeholder="Hoboken Liquor" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="smtp-from-name-input" /></div>
          </div>
          <div className="flex items-center justify-between mt-4 bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3">
            <div>
              <p className="text-white text-sm font-medium">Use TLS (STARTTLS)</p>
              <p className="text-[#64748B] text-xs">Recommended — required for Gmail / SendGrid / most modern servers</p>
            </div>
            <Switch checked={settings.smtp_use_tls !== false} onCheckedChange={v => updateSetting('smtp_use_tls', v)} data-testid="smtp-tls-toggle" />
          </div>
          <div className="bg-[#b56b38]/10 border border-[#b56b38]/20 rounded-lg px-3 py-2 mt-3">
            <p className="text-[#94A3B8] text-[11px]"><strong className="text-[#b56b38]">Gmail quick-start:</strong> Host <code>smtp.gmail.com</code>, Port <code>587</code>, TLS ON, Username = your Gmail, Password = <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-[#b56b38] underline">App Password</a> (not your regular password).</p>
          </div>
          <div className="flex gap-2 mt-4">
            <input value={settings._test_email || ''} onChange={e => updateSetting('_test_email', e.target.value)} placeholder="Send test email to..." type="email" className="flex-1 bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="smtp-test-recipient" />
            <button onClick={async () => {
              try {
                await api.put('/settings', { settings: Object.fromEntries(Object.entries(settings).filter(([k]) => !k.startsWith('_'))) });
                const res = await api.post('/admin/email/test', { to: settings._test_email || user?.email });
                toast.success(res.data.message || 'Sent');
              } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
            }} className="bg-[#b56b38] hover:bg-[#d8964f] text-white rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2 whitespace-nowrap" data-testid="smtp-send-test-btn"><Mail className="w-4 h-4" /> Save & Send Test</button>
          </div>
        </div>

        {/* Payment Methods — DEDICATED, PROMINENT CARD */}
        <div className="bg-[#12151C] border border-[#b56b38]/30 rounded-xl p-5 lg:col-span-2" data-testid="payment-methods-card">
          <h3 className="text-white font-medium mb-2 flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#b56b38]" /> Payment Methods</h3>
          <p className="text-[#64748B] text-xs mb-4">Turn customer-facing payment methods on or off. These toggles control what shows up at Checkout.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3">
              <div>
                <p className="text-white text-sm font-medium">Cash on Delivery / at Pickup</p>
                <p className="text-[#64748B] text-xs">Driver or store staff collects cash and marks as paid.</p>
              </div>
              <Switch checked={settings.cod_enabled !== false} onCheckedChange={v => updateSetting('cod_enabled', v)} data-testid="payment-cod-toggle" />
            </div>
            <div className="flex items-center justify-between bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3">
              <div>
                <p className="text-white text-sm font-medium">Allow Pickup Orders</p>
                <p className="text-[#64748B] text-xs">Show in-store pickup as a fulfillment option at Checkout.</p>
              </div>
              <Switch checked={settings.pickup_enabled !== false} onCheckedChange={v => updateSetting('pickup_enabled', v)} data-testid="payment-pickup-toggle" />
            </div>
            <div className="flex items-center justify-between bg-[#0B0E14] border border-white/10 rounded-lg px-4 py-3">
              <div>
                <p className="text-white text-sm font-medium">Allow Online Card Payments (Stripe)</p>
                <p className="text-[#64748B] text-xs">Enable card processing via Stripe at checkout.</p>
              </div>
              <Switch checked={settings.online_enabled === true} onCheckedChange={v => updateSetting('online_enabled', v)} data-testid="payment-stripe-toggle" />
            </div>
          </div>

          {settings.online_enabled && (
            <div className="mt-6 border-t border-white/10 pt-4 space-y-4">
              <h4 className="text-white font-medium text-sm flex items-center gap-2">Stripe API Configuration</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-[#64748B] text-xs mb-1 block font-medium">Publishable Key</label>
                  <input value={settings.stripe_pk || ''} onChange={e => updateSetting('stripe_pk', e.target.value)} placeholder="pk_test_..." className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono" data-testid="stripe-pk-input" />
                </div>
                <div>
                  <label className="text-[#64748B] text-xs mb-1 block flex items-center justify-between font-medium">
                    <span>Secret Key</span>
                    {settings.stripe_sk === '__SECRET_SET__' && <span className="text-[10px] text-[#10B981]">✓ Saved · type to replace</span>}
                  </label>
                  <input
                    value={settings.stripe_sk === '__SECRET_SET__' ? '' : (settings.stripe_sk || '')}
                    onChange={e => updateSetting('stripe_sk', e.target.value)}
                    placeholder={settings.stripe_sk === '__SECRET_SET__' ? '••••••••••••  (leave blank to keep current)' : 'sk_test_...'}
                    type="password"
                    className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono"
                    data-testid="stripe-sk-input"
                  />
                </div>
                <div>
                  <label className="text-[#64748B] text-xs mb-1 block flex items-center justify-between font-medium">
                    <span>Webhook Secret</span>
                    {settings.stripe_webhook_secret === '__SECRET_SET__' && <span className="text-[10px] text-[#10B981]">✓ Saved · type to replace</span>}
                  </label>
                  <input
                    value={settings.stripe_webhook_secret === '__SECRET_SET__' ? '' : (settings.stripe_webhook_secret || '')}
                    onChange={e => updateSetting('stripe_webhook_secret', e.target.value)}
                    placeholder={settings.stripe_webhook_secret === '__SECRET_SET__' ? '••••••••••••  (leave blank to keep current)' : 'whsec_...'}
                    type="password"
                    className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono"
                    data-testid="stripe-webhook-secret-input"
                  />
                </div>
              </div>
            </div>
          )}
        </div>



        {/* API Keys - Twilio */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-medium mb-2">Twilio SMS</h3>
          <p className="text-[#64748B] text-xs mb-4">Add Twilio credentials for SMS notifications</p>
          <div className="space-y-3">
            <div><label className="text-[#64748B] text-xs mb-1 block">Account SID</label><input value={settings.twilio_sid || ''} onChange={e => updateSetting('twilio_sid', e.target.value)} placeholder="AC..." className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="twilio-sid-input" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block flex items-center justify-between"><span>Auth Token</span>{settings.twilio_token === '__SECRET_SET__' && <span className="text-[10px] text-[#10B981]">✓ Saved · type to replace</span>}</label><input value={settings.twilio_token === '__SECRET_SET__' ? '' : (settings.twilio_token || '')} onChange={e => updateSetting('twilio_token', e.target.value)} placeholder={settings.twilio_token === '__SECRET_SET__' ? '••••••••••••  (leave blank to keep current)' : 'Token'} type="password" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block">Phone Number</label><input value={settings.twilio_phone || ''} onChange={e => updateSetting('twilio_phone', e.target.value)} placeholder="+1..." className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" /></div>
          </div>
        </div>

        {/* DoorDash Drive */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5 lg:col-span-2">
          <h3 className="text-white font-medium mb-2">DoorDash Drive API</h3>
          <p className="text-[#64748B] text-xs mb-4">Configure DoorDash Drive for overflow delivery dispatching</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><label className="text-[#64748B] text-xs mb-1 block">Developer ID</label><input value={settings.doordash_dev_id || ''} onChange={e => updateSetting('doordash_dev_id', e.target.value)} placeholder="Developer ID" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="doordash-dev-input" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block">Key ID</label><input value={settings.doordash_key_id || ''} onChange={e => updateSetting('doordash_key_id', e.target.value)} placeholder="Key ID" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block flex items-center justify-between"><span>Signing Secret</span>{settings.doordash_secret === '__SECRET_SET__' && <span className="text-[10px] text-[#10B981]">✓ Saved</span>}</label><input value={settings.doordash_secret === '__SECRET_SET__' ? '' : (settings.doordash_secret || '')} onChange={e => updateSetting('doordash_secret', e.target.value)} placeholder={settings.doordash_secret === '__SECRET_SET__' ? '••••••••••••  (leave blank to keep current)' : 'Signing Secret'} type="password" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" /></div>
          </div>
        </div>

        {/* Account Settings — change login email, password, forgot-password link */}
        <div className="lg:col-span-2" data-testid="admin-account-settings">
          <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Key className="w-4 h-4 text-[#b56b38]" /> Admin Account</h3>
          <AccountSettings />
        </div>
      </div>
    </div>
  );
}

function PromotionsTab() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCoupon, setEditCoupon] = useState(null);
  const [form, setForm] = useState({ code: '', type: 'percent', value: '', min_order: '', usage_limit: '', per_customer_limit: '1', expiry_date: '', active: true });

  useEffect(() => { fetchCoupons(); }, []);
  const fetchCoupons = async () => {
    try { const res = await api.get('/admin/coupons'); setCoupons(res.data.coupons); } catch {} finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ code: '', type: 'percent', value: '', min_order: '', usage_limit: '', per_customer_limit: '1', expiry_date: '', active: true });
    setEditCoupon(null);
  };

  const handleEdit = (coupon) => {
    setForm({
      code: coupon.code,
      type: coupon.type,
      value: String(coupon.value),
      min_order: String(coupon.min_order),
      usage_limit: String(coupon.usage_limit || ''),
      per_customer_limit: String(coupon.per_customer_limit || 1),
      expiry_date: coupon.expiry_date || '',
      active: coupon.active
    });
    setEditCoupon(coupon);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      code: form.code,
      type: form.type,
      value: parseFloat(form.value) || 0,
      min_order: parseFloat(form.min_order) || 0,
      usage_limit: parseInt(form.usage_limit) || 0,
      per_customer_limit: parseInt(form.per_customer_limit) || 1,
      expiry_date: form.expiry_date,
      active: form.active
    };
    try {
      if (editCoupon) {
        await api.put(`/admin/coupons/${editCoupon.id}`, payload);
        toast.success('Coupon updated');
      } else {
        await api.post('/admin/coupons', payload);
        toast.success('Coupon created');
      }
      setShowForm(false);
      resetForm();
      fetchCoupons();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete coupon?')) return;
    try { await api.delete(`/admin/coupons/${id}`); toast.success('Deleted'); fetchCoupons(); } catch { toast.error('Failed'); }
  };

  const toggleActive = async (coupon) => {
    try { await api.put(`/admin/coupons/${coupon.id}`, { active: !coupon.active }); fetchCoupons(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to toggle coupon'); }
  };

  return (
    <div data-testid="admin-promotions">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Playfair_Display'] text-2xl text-white">Promotions & Coupons ({coupons.length})</h2>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2" data-testid="add-coupon-btn"><Plus className="w-4 h-4" /> Create Coupon</button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => { setShowForm(false); resetForm(); }}>
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h3 className="font-['Playfair_Display'] text-xl text-white">{editCoupon ? 'Edit' : 'Create'} Coupon</h3>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="text-[#64748B]"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input value={form.code} onChange={e => setForm({...form, code: e.target.value})} placeholder="Coupon Code *" required className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white uppercase" data-testid="coupon-code-input" />
              <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white">
                <option value="percent">Percentage Off</option><option value="amount">Dollar Amount Off</option><option value="free_delivery">Free Delivery</option>
              </select>
              {form.type !== 'free_delivery' && <input value={form.value} onChange={e => setForm({...form, value: e.target.value})} placeholder={form.type === 'percent' ? 'Discount %' : 'Discount $'} type="number" step="0.01" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" />}
              <div className="grid grid-cols-2 gap-3">
                <input value={form.min_order} onChange={e => setForm({...form, min_order: e.target.value})} placeholder="Min Order $" type="number" step="0.01" className="bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
                <input value={form.usage_limit} onChange={e => setForm({...form, usage_limit: e.target.value})} placeholder="Usage Limit" type="number" className="bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
              </div>
              <input value={form.expiry_date} onChange={e => setForm({...form, expiry_date: e.target.value})} type="date" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
              <button type="submit" className="w-full bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-2.5 font-medium" data-testid="save-coupon-btn">{editCoupon ? 'Update' : 'Create'} Coupon</button>
            </form>
          </div>
        </div>
      )}

      <div className="bg-[#12151C] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="coupons-table">
            <thead><tr className="border-b border-white/5">
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Code</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Type</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Value</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Min Order</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Used</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Status</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Actions</th>
            </tr></thead>
            <tbody>
              {coupons.map((c, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-[#b56b38] font-mono font-medium">{c.code}</td>
                  <td className="px-4 py-3 text-white capitalize">{c.type === 'free_delivery' ? 'Free Delivery' : c.type}</td>
                  <td className="px-4 py-3 text-white">{c.type === 'percent' ? `${c.value}%` : c.type === 'amount' ? `$${c.value}` : '-'}</td>
                  <td className="px-4 py-3 text-[#94A3B8]">${c.min_order || 0}</td>
                  <td className="px-4 py-3 text-[#94A3B8]">{c.times_used || 0}{c.usage_limit ? `/${c.usage_limit}` : ''}</td>
                  <td className="px-4 py-3"><button onClick={() => toggleActive(c)} className={`px-2 py-0.5 rounded-full text-xs ${c.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{c.active ? 'Active' : 'Inactive'}</button></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(c)} className="text-[#94A3B8] hover:text-white" data-testid={`edit-coupon-${i}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(c.id)} className="text-[#64748B] hover:text-[#EF4444]" data-testid={`delete-coupon-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {coupons.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#64748B]">No coupons yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ZonesTab() {
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editZone, setEditZone] = useState(null);
  const [form, setForm] = useState({ name: '', delivery_fee: '', min_order: '', estimated_time: '30-45 min', active: true });

  useEffect(() => { fetchZones(); }, []);
  const fetchZones = async () => {
    try { const res = await api.get('/admin/zones'); setZones(res.data.zones); } catch {} finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ name: '', delivery_fee: '', min_order: '', estimated_time: '30-45 min', active: true });
    setEditZone(null);
  };

  const handleEdit = (zone) => {
    setForm({
      name: zone.name,
      delivery_fee: String(zone.delivery_fee),
      min_order: String(zone.min_order),
      estimated_time: zone.estimated_time || '30-45 min',
      active: zone.active
    });
    setEditZone(zone);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      delivery_fee: parseFloat(form.delivery_fee) || 0,
      min_order: parseFloat(form.min_order) || 0,
      estimated_time: form.estimated_time || '30-45 min',
      active: form.active
    };
    try {
      if (editZone) {
        await api.put(`/admin/zones/${editZone.id}`, payload);
        toast.success('Zone updated');
      } else {
        await api.post('/admin/zones', payload);
        toast.success('Zone created');
      }
      setShowForm(false);
      resetForm();
      fetchZones();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete zone?')) return;
    try { await api.delete(`/admin/zones/${id}`); toast.success('Deleted'); fetchZones(); } catch { toast.error('Failed'); }
  };

  const toggleActive = async (zone) => {
    try { await api.put(`/admin/zones/${zone.id}`, { active: !zone.active }); fetchZones(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to toggle zone'); }
  };

  return (
    <div data-testid="admin-zones">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Playfair_Display'] text-2xl text-white">Delivery Zones ({zones.length})</h2>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2" data-testid="add-zone-btn"><Plus className="w-4 h-4" /> Add Zone</button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => { setShowForm(false); resetForm(); }}>
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h3 className="font-['Playfair_Display'] text-xl text-white">{editZone ? 'Edit' : 'Add'} Zone</h3>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="text-[#64748B]"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Zone Name *" required className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="zone-name-input" />
              <div className="grid grid-cols-2 gap-3">
                <input value={form.delivery_fee} onChange={e => setForm({...form, delivery_fee: e.target.value})} placeholder="Delivery Fee $" type="number" step="0.01" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
                <input value={form.min_order} onChange={e => setForm({...form, min_order: e.target.value})} placeholder="Min Order $" type="number" step="0.01" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
              </div>
              <input value={form.estimated_time} onChange={e => setForm({...form, estimated_time: e.target.value})} placeholder="Estimated Time" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
              <button type="submit" className="w-full bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-2.5 font-medium" data-testid="save-zone-btn">{editZone ? 'Update' : 'Create'} Zone</button>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {zones.map((z, i) => (
          <div key={i} className="bg-[#12151C] border border-white/10 rounded-xl p-5" data-testid={`zone-card-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-medium flex items-center gap-2"><MapPin className="w-4 h-4 text-[#b56b38]" /> {z.name}</h3>
              <button onClick={() => toggleActive(z)} className={`w-2.5 h-2.5 rounded-full ${z.active ? 'bg-[#10B981]' : 'bg-[#64748B]'}`} />
            </div>
            <div className="space-y-1 text-sm">
              <p className="text-[#94A3B8]">Delivery Fee: <span className="text-white">${z.delivery_fee}</span></p>
              <p className="text-[#94A3B8]">Min Order: <span className="text-white">${z.min_order}</span></p>
              <p className="text-[#94A3B8]">Est. Time: <span className="text-white">{z.estimated_time}</span></p>
            </div>
            <div className="mt-3 flex gap-3 border-t border-white/5 pt-3">
              <button onClick={() => handleEdit(z)} className="text-[#94A3B8] hover:text-white text-xs flex items-center gap-1" data-testid={`edit-zone-${i}`}><Pencil className="w-3 h-3" /> Edit</button>
              <button onClick={() => handleDelete(z.id)} className="text-[#64748B] hover:text-[#EF4444] text-xs flex items-center gap-1" data-testid={`delete-zone-${i}`}><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        ))}
        {zones.length === 0 && <p className="text-[#64748B] col-span-3 text-center py-8">No delivery zones configured</p>}
      </div>
    </div>
  );
}

function CategoriesTab() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', image: '', active: true });

  useEffect(() => { fetchCategories(); }, []);
  const fetchCategories = async () => {
    try { const res = await api.get('/admin/categories'); setCategories(res.data.categories); } catch {} finally { setLoading(false); }
  };

  const resetForm = () => { setForm({ name: '', description: '', image: '', active: true }); setEditCat(null); };

  const handleEdit = (cat) => {
    setForm({ name: cat.name, description: cat.description || '', image: cat.image || '', active: cat.active });
    setEditCat(cat);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editCat) {
        await api.put(`/admin/categories/${editCat.id}`, form);
        toast.success('Category updated');
      } else {
        await api.post('/admin/categories', form);
        toast.success('Category created');
      }
      setShowForm(false); resetForm(); fetchCategories();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? Products in this category will not be deleted.')) return;
    try { await api.delete(`/admin/categories/${id}`); toast.success('Deleted'); fetchCategories(); } catch { toast.error('Failed'); }
  };

  const toggleActive = async (cat) => {
    try { await api.put(`/admin/categories/${cat.id}`, { active: !cat.active }); fetchCategories(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to toggle category'); }
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Playfair_Display'] text-2xl text-white">Categories ({categories.length})</h2>
        <button onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full px-4 py-2 text-sm font-medium flex items-center gap-2" data-testid="add-category-btn"><Plus className="w-4 h-4" /> Add Category</button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h3 className="font-['Playfair_Display'] text-xl text-white">{editCat ? 'Edit' : 'Add'} Category</h3>
              <button onClick={() => setShowForm(false)} className="text-[#64748B]"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Category Name *" required className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="category-name-input" />
              <input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Description (optional)" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
              <ImageUpload value={form.image || ''} onChange={url => setForm({...form, image: url})} label="Category Image" />
              <label className="flex items-center gap-2 text-sm text-[#94A3B8]"><input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} className="accent-[#cc3737]" /> Active</label>
              <button type="submit" className="w-full bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full py-2.5 font-medium" data-testid="save-category-btn">{editCat ? 'Update' : 'Create'} Category</button>
            </form>
          </div>
        </div>
      )}

      <div className="bg-[#12151C] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="categories-table">
            <thead><tr className="border-b border-white/5">
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Order</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Name</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Description</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Status</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Actions</th>
            </tr></thead>
            <tbody>
              {categories.map((cat, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-[#64748B]">{cat.sort_order !== undefined ? cat.sort_order + 1 : i + 1}</td>
                  <td className="px-4 py-3 text-white font-medium">{cat.name}</td>
                  <td className="px-4 py-3 text-[#94A3B8]">{cat.description || '-'}</td>
                  <td className="px-4 py-3"><button onClick={() => toggleActive(cat)} className={`px-2 py-0.5 rounded-full text-xs ${cat.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{cat.active ? 'Active' : 'Inactive'}</button></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(cat)} className="text-[#94A3B8] hover:text-white" data-testid={`edit-category-${i}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(cat.id)} className="text-[#64748B] hover:text-[#EF4444]" data-testid={`delete-category-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#64748B]">No categories yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function HoursTab() {
  const [settings, setSettings] = useState({});
  const [happyHours, setHappyHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hhForm, setHhForm] = useState({ category: 'Beer & Wine', discount_percent: '15', start_time: '16:00', end_time: '18:00', days: ['mon','tue','wed','thu','fri'] });
  const DAYS = ['mon','tue','wed','thu','fri','sat','sun'];

  useEffect(() => {
    Promise.all([
      api.get('/settings').then(r => setSettings(r.data.settings)),
      api.get('/admin/happy-hours').then(r => setHappyHours(r.data.rules))
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const saveHours = async () => {
    setSaving(true);
    try { await api.put('/settings', { settings: { store_hours: settings.store_hours } }); toast.success('Store hours saved'); } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const updateDayHours = (day, field, value) => {
    const hours = { ...(settings.store_hours || {}) };
    hours[day] = { ...(hours[day] || { open: '09:00', close: '22:00' }), [field]: value };
    setSettings({ ...settings, store_hours: hours });
  };

  const createHappyHour = async () => {
    try { const res = await api.post('/admin/happy-hours', { ...hhForm, discount_percent: parseInt(hhForm.discount_percent) }); setHappyHours(prev => [...prev, res.data]); toast.success('Happy hour created'); } catch { toast.error('Failed'); }
  };

  const deleteHappyHour = async (id) => {
    try { await api.delete(`/admin/happy-hours/${id}`); setHappyHours(prev => prev.filter(r => r.id !== id)); toast.success('Deleted'); } catch { toast.error('Failed'); }
  };

  if (loading) return <LoadingSkeleton />;

  return (
    <div data-testid="admin-hours">
      <h2 className="font-['Playfair_Display'] text-2xl text-white mb-6">Store Hours & Happy Hour</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Store Hours */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-[#b56b38]" /> Operating Hours</h3>
          <div className="space-y-2">
            {DAYS.map(day => {
              const h = settings.store_hours?.[day] || { open: '09:00', close: '22:00' };
              return (
                <div key={day} className="flex items-center gap-3" data-testid={`hours-${day}`}>
                  <span className="text-[#94A3B8] text-sm w-10 capitalize">{day}</span>
                  <input type="time" value={h.open} onChange={e => updateDayHours(day, 'open', e.target.value)} className="bg-[#0B0E14] border border-white/10 rounded px-2 py-1 text-sm text-white" />
                  <span className="text-[#64748B]">to</span>
                  <input type="time" value={h.close} onChange={e => updateDayHours(day, 'close', e.target.value)} className="bg-[#0B0E14] border border-white/10 rounded px-2 py-1 text-sm text-white" />
                </div>
              );
            })}
          </div>
          <button onClick={saveHours} disabled={saving} className="mt-4 bg-[#cc3737] hover:bg-[#d94444] disabled:opacity-50 text-white rounded-full px-6 py-2 text-sm font-medium" data-testid="save-hours-btn">{saving ? 'Saving...' : 'Save Hours'}</button>
        </div>

        {/* Happy Hour Rules */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2"><Tag className="w-4 h-4 text-[#b56b38]" /> Happy Hour Pricing</h3>
          {/* Existing rules */}
          {happyHours.map((rule, i) => (
            <div key={i} className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2 mb-2" data-testid={`happy-hour-${i}`}>
              <div><span className="text-white text-sm">{rule.category}</span><span className="text-[#10B981] text-sm ml-2">{rule.discount_percent}% off</span><p className="text-[#64748B] text-xs">{rule.start_time} - {rule.end_time} · {rule.days?.join(', ')}</p></div>
              <button onClick={() => deleteHappyHour(rule.id)} className="text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {/* Create new */}
          <div className="border-t border-white/5 pt-3 mt-3 space-y-2">
            <select value={hhForm.category} onChange={e => setHhForm({...hhForm, category: e.target.value})} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-1.5 text-sm text-white"><option>Liquor & Spirits</option><option>Beer & Wine</option><option>Grocery</option><option>Deli & Hot Food</option></select>
            <div className="grid grid-cols-3 gap-2">
              <input value={hhForm.discount_percent} onChange={e => setHhForm({...hhForm, discount_percent: e.target.value})} placeholder="% off" type="number" className="bg-[#0B0E14] border border-white/10 rounded-md px-2 py-1.5 text-sm text-white" />
              <input type="time" value={hhForm.start_time} onChange={e => setHhForm({...hhForm, start_time: e.target.value})} className="bg-[#0B0E14] border border-white/10 rounded-md px-2 py-1.5 text-sm text-white" />
              <input type="time" value={hhForm.end_time} onChange={e => setHhForm({...hhForm, end_time: e.target.value})} className="bg-[#0B0E14] border border-white/10 rounded-md px-2 py-1.5 text-sm text-white" />
            </div>
            <div className="flex gap-1 flex-wrap">{DAYS.map(d => (<button key={d} onClick={() => setHhForm({...hhForm, days: hhForm.days.includes(d) ? hhForm.days.filter(x=>x!==d) : [...hhForm.days, d]})} className={`px-2 py-1 rounded text-xs ${hhForm.days.includes(d) ? 'bg-[#b56b38] text-white' : 'bg-[#0B0E14] text-[#64748B]'}`}>{d}</button>))}</div>
            <button onClick={createHappyHour} className="w-full bg-[#b56b38] hover:bg-[#d8964f] text-white rounded-full py-2 text-sm font-medium" data-testid="create-happy-hour-btn">Create Happy Hour Rule</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContentTab() {
  const [pages, setPages] = useState({});
  const [announcement, setAnnouncement] = useState('');
  const [socialLinks, setSocialLinks] = useState({});
  const [heroTitle, setHeroTitle] = useState('');
  const [heroSubtitle, setHeroSubtitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [editPage, setEditPage] = useState(null);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/content/terms').then(r => setPages(p => ({...p, terms: r.data.content}))),
      api.get('/content/privacy').then(r => setPages(p => ({...p, privacy: r.data.content}))),
      api.get('/content/delivery_policy').then(r => setPages(p => ({...p, delivery_policy: r.data.content}))),
      api.get('/content/announcement').then(r => setAnnouncement(r.data.content || '')),
      api.get('/content/hero_title').then(r => setHeroTitle(r.data.content || '')),
      api.get('/content/hero_subtitle').then(r => setHeroSubtitle(r.data.content || '')),
      api.get('/settings').then(r => setSocialLinks({instagram: r.data.settings?.social_instagram || '', facebook: r.data.settings?.social_facebook || '', twitter: r.data.settings?.social_twitter || ''})),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const savePage = async (key) => {
    try { await api.put(`/admin/content/${key}`, { content: key === editPage ? editContent : pages[key] }); toast.success('Content saved'); setEditPage(null); } catch { toast.error('Failed'); }
  };

  const saveAnnouncement = async () => {
    try { await api.put('/admin/content/announcement', { content: announcement }); toast.success('Announcement saved'); } catch { toast.error('Failed'); }
  };

  const saveHero = async () => {
    try { await Promise.all([api.put('/admin/content/hero_title', { content: heroTitle }), api.put('/admin/content/hero_subtitle', { content: heroSubtitle })]); toast.success('Hero content saved'); } catch { toast.error('Failed'); }
  };

  const saveSocial = async () => {
    try { await api.put('/settings', { settings: { social_instagram: socialLinks.instagram, social_facebook: socialLinks.facebook, social_twitter: socialLinks.twitter } }); toast.success('Social links saved'); } catch { toast.error('Failed'); }
  };

  if (loading) return <LoadingSkeleton />;

  return (
    <div data-testid="admin-content">
      <h2 className="font-['Playfair_Display'] text-2xl text-white mb-6">Content & Pages</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Announcement Banner */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Megaphone className="w-4 h-4 text-[#b56b38]" /> Store Announcement</h3>
          <p className="text-[#64748B] text-xs mb-3">Shown as a banner to all customers. Leave empty to hide.</p>
          <textarea value={announcement} onChange={e => setAnnouncement(e.target.value)} rows={3} placeholder="e.g., Holiday hours: Closed Dec 25. Happy New Year!" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white resize-none" data-testid="announcement-input" />
          <button onClick={saveAnnouncement} className="mt-2 bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full px-4 py-2 text-sm font-medium" data-testid="save-announcement-btn">Save Announcement</button>
        </div>

        {/* Social Media Links */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-medium mb-3">Social Media Links</h3>
          <div className="space-y-2">
            <input value={socialLinks.instagram} onChange={e => setSocialLinks({...socialLinks, instagram: e.target.value})} placeholder="Instagram URL" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="social-instagram" />
            <input value={socialLinks.facebook} onChange={e => setSocialLinks({...socialLinks, facebook: e.target.value})} placeholder="Facebook URL" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="social-facebook" />
            <input value={socialLinks.twitter} onChange={e => setSocialLinks({...socialLinks, twitter: e.target.value})} placeholder="Twitter/X URL" className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="social-twitter" />
          </div>
          <button onClick={saveSocial} className="mt-2 bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full px-4 py-2 text-sm font-medium" data-testid="save-social-btn">Save Links</button>
        </div>

        {/* Hero Content Editor */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-medium mb-3">Landing Page Hero</h3>
          <div className="space-y-2">
            <div><label className="text-[#64748B] text-xs mb-1 block">Hero Title</label><textarea value={heroTitle} onChange={e => setHeroTitle(e.target.value)} rows={2} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white resize-none" data-testid="hero-title-input" /></div>
            <div><label className="text-[#64748B] text-xs mb-1 block">Hero Subtitle</label><textarea value={heroSubtitle} onChange={e => setHeroSubtitle(e.target.value)} rows={2} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white resize-none" data-testid="hero-subtitle-input" /></div>
          </div>
          <button onClick={saveHero} className="mt-2 bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full px-4 py-2 text-sm font-medium" data-testid="save-hero-btn">Save Hero Content</button>
        </div>

        {/* Legal Pages Editor */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-medium mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-[#b56b38]" /> Legal Pages</h3>
          {[{key: 'terms', label: 'Terms of Service'}, {key: 'privacy', label: 'Privacy Policy'}, {key: 'delivery_policy', label: 'Delivery Policy'}].map(page => (
            <div key={page.key} className="mb-3">
              {editPage === page.key ? (
                <div>
                  <label className="text-[#94A3B8] text-xs mb-1 block">{page.label}</label>
                  <textarea value={editContent} onChange={e => setEditContent(e.target.value)} rows={6} className="w-full bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white resize-none" data-testid={`edit-${page.key}`} />
                  <div className="flex gap-2 mt-1"><button onClick={() => savePage(page.key)} className="bg-[#cc3737] text-white rounded-full px-4 py-1.5 text-xs">Save</button><button onClick={() => setEditPage(null)} className="text-[#64748B] text-xs">Cancel</button></div>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2">
                  <span className="text-white text-sm">{page.label}</span>
                  <button onClick={() => { setEditPage(page.key); setEditContent(pages[page.key] || ''); }} className="text-[#b56b38] text-xs hover:underline" data-testid={`edit-${page.key}-btn`}>Edit</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SecurityTab() {
  const [auditLogs, setAuditLogs] = useState([]);
  const [blacklist, setBlacklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPhone, setNewPhone] = useState('');
  const [newReason, setNewReason] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/admin/audit-log').then(r => setAuditLogs(r.data.logs)),
      api.get('/admin/blacklist').then(r => setBlacklist(r.data.blacklist))
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const addToBlacklist = async () => {
    if (!newPhone.trim()) return toast.error('Phone number required');
    try { await api.post('/admin/blacklist', { phone: newPhone, reason: newReason }); setBlacklist(prev => [...prev, { phone: newPhone, reason: newReason }]); setNewPhone(''); setNewReason(''); toast.success('Phone blacklisted'); } catch { toast.error('Failed'); }
  };

  const removeBlacklist = async (phone) => {
    try { await api.delete(`/admin/blacklist/${phone}`); setBlacklist(prev => prev.filter(b => b.phone !== phone)); toast.success('Removed'); } catch { toast.error('Failed'); }
  };

  if (loading) return <LoadingSkeleton />;

  return (
    <div data-testid="admin-security">
      <h2 className="font-['Playfair_Display'] text-2xl text-white mb-6">Security & Audit</h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Blacklist */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2"><Ban className="w-4 h-4 text-[#EF4444]" /> Phone Blacklist</h3>
          <p className="text-[#64748B] text-xs mb-3">Blocked phone numbers cannot register</p>
          {blacklist.map((b, i) => (
            <div key={i} className="flex items-center justify-between bg-[#0B0E14] rounded-lg px-3 py-2 mb-2" data-testid={`blacklist-${i}`}>
              <div><span className="text-white text-sm font-mono">{b.phone}</span>{b.reason && <span className="text-[#64748B] text-xs ml-2">({b.reason})</span>}</div>
              <button onClick={() => removeBlacklist(b.phone)} className="text-[#64748B] hover:text-[#EF4444]"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <div className="flex gap-2 mt-2">
            <input value={newPhone} onChange={e => setNewPhone(e.target.value)} placeholder="Phone number" className="flex-1 bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="blacklist-phone-input" />
            <input value={newReason} onChange={e => setNewReason(e.target.value)} placeholder="Reason" className="flex-1 bg-[#0B0E14] border border-white/10 rounded-md px-3 py-2 text-sm text-white" />
            <button onClick={addToBlacklist} className="bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-md px-3 py-2 text-sm font-medium" data-testid="add-blacklist-btn">Block</button>
          </div>
        </div>

        {/* Audit Log */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2"><Shield className="w-4 h-4 text-[#b56b38]" /> Audit Log</h3>
          <div className="max-h-96 overflow-y-auto space-y-2">
            {auditLogs.length === 0 && <p className="text-[#64748B] text-sm">No audit entries yet. Actions will appear here.</p>}
            {auditLogs.map((log, i) => (
              <div key={i} className="bg-[#0B0E14] rounded-lg px-3 py-2" data-testid={`audit-log-${i}`}>
                <div className="flex justify-between"><span className="text-[#b56b38] text-xs font-medium">{log.admin_name}</span><span className="text-[#64748B] text-[10px]">{new Date(log.timestamp).toLocaleString()}</span></div>
                <p className="text-white text-sm">{log.action}</p>
                {log.details && <p className="text-[#64748B] text-xs">{log.details}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CashReconciliationTab() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get(`/admin/cash-reconciliation?date=${date}`); setData(res.data); } catch { toast.error('Failed to load report'); } finally { setLoading(false); }
  }, [date]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportCSV = () => {
    if (!data) return;
    const rows = [['Order #', 'Collector', 'Role', 'Type', 'Customer', 'Expected', 'Collected', 'Variance', 'Closed At']];
    data.orders.forEach(o => rows.push([o.order_number, o.collector, o.role, o.fulfillment_type, o.customer_name || '', o.expected.toFixed(2), o.collected.toFixed(2), o.variance.toFixed(2), o.closed_at || '']));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `cash-reconciliation-${data.date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  if (loading && !data) return <LoadingSkeleton />;
  if (!data) return <p className="text-[#64748B]">Failed to load</p>;

  const s = data.summary;
  const varianceColor = s.total_variance === 0 ? 'text-[#10B981]' : s.total_variance > 0 ? 'text-[#b56b38]' : 'text-[#EF4444]';

  return (
    <div data-testid="admin-cash-reconciliation">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-['Playfair_Display'] text-2xl text-white">Daily Cash Reconciliation</h2>
          <p className="text-[#64748B] text-xs mt-1">Track cash collected by drivers (delivery) and staff (pickup counter) on any given day.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-[#12151C] border border-white/10 rounded-md px-3 py-1.5 text-sm text-white" data-testid="cash-date-picker" />
          <button onClick={fetchReport} className="p-2 bg-[#12151C] border border-white/10 rounded-md text-[#94A3B8] hover:text-white" data-testid="cash-refresh-btn"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={exportCSV} disabled={!data.orders?.length} className="bg-[#b56b38] hover:bg-[#d8964f] disabled:opacity-40 text-white rounded-md px-3 py-1.5 text-sm font-medium flex items-center gap-1.5" data-testid="cash-export-btn"><FileText className="w-4 h-4" /> Export CSV</button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-4" data-testid="cash-kpi-orders">
          <ShoppingCart className="w-5 h-5 text-[#3B82F6] mb-2" />
          <p className="text-2xl font-semibold text-white">{s.total_cod_orders}</p>
          <p className="text-[#64748B] text-xs mt-1">COD Orders</p>
        </div>
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-4" data-testid="cash-kpi-expected">
          <DollarSign className="w-5 h-5 text-[#94A3B8] mb-2" />
          <p className="text-2xl font-semibold text-white">${s.total_expected.toFixed(2)}</p>
          <p className="text-[#64748B] text-xs mt-1">Expected Cash</p>
        </div>
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-4" data-testid="cash-kpi-collected">
          <DollarSign className="w-5 h-5 text-[#10B981] mb-2" />
          <p className="text-2xl font-semibold text-white">${s.total_collected.toFixed(2)}</p>
          <p className="text-[#64748B] text-xs mt-1">Collected Cash</p>
        </div>
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-4" data-testid="cash-kpi-variance">
          <AlertCircle className={`w-5 h-5 ${varianceColor} mb-2`} />
          <p className={`text-2xl font-semibold ${varianceColor}`}>{s.total_variance >= 0 ? '+' : ''}${s.total_variance.toFixed(2)}</p>
          <p className="text-[#64748B] text-xs mt-1">Variance (over/short)</p>
        </div>
      </div>

      {/* By Collector */}
      <h3 className="text-white font-medium mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-[#b56b38]" /> By Collector</h3>
      <div className="bg-[#12151C] border border-white/10 rounded-xl overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cash-by-collector-table">
            <thead><tr className="border-b border-white/5">
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Collector</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Role</th>
              <th className="text-right px-4 py-3 text-[#64748B] font-medium">Orders</th>
              <th className="text-right px-4 py-3 text-[#64748B] font-medium">Expected</th>
              <th className="text-right px-4 py-3 text-[#64748B] font-medium">Collected</th>
              <th className="text-right px-4 py-3 text-[#64748B] font-medium">Variance</th>
            </tr></thead>
            <tbody>
              {data.by_collector.map((c, i) => {
                const vColor = c.variance === 0 ? 'text-[#10B981]' : c.variance > 0 ? 'text-[#b56b38]' : 'text-[#EF4444]';
                return (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`cash-collector-${i}`}>
                    <td className="px-4 py-3 text-white font-medium">{c.name}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${c.role === 'driver' ? 'bg-amber-500/20 text-amber-400' : 'bg-[#b56b38]/20 text-[#b56b38]'}`}>{c.role === 'driver' ? 'Driver' : 'Staff'}</span></td>
                    <td className="px-4 py-3 text-right text-[#94A3B8]">{c.orders}</td>
                    <td className="px-4 py-3 text-right text-white">${c.expected.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-white">${c.collected.toFixed(2)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${vColor}`}>{c.variance >= 0 ? '+' : ''}${c.variance.toFixed(2)}</td>
                  </tr>
                );
              })}
              {data.by_collector.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[#64748B]">No COD cash was collected on this date</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order breakdown */}
      <h3 className="text-white font-medium mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-[#b56b38]" /> Order Breakdown ({data.orders.length})</h3>
      <div className="bg-[#12151C] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cash-orders-table">
            <thead><tr className="border-b border-white/5">
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Order #</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Type</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Customer</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Collector</th>
              <th className="text-right px-4 py-3 text-[#64748B] font-medium">Expected</th>
              <th className="text-right px-4 py-3 text-[#64748B] font-medium">Collected</th>
              <th className="text-right px-4 py-3 text-[#64748B] font-medium">Variance</th>
              <th className="text-left px-4 py-3 text-[#64748B] font-medium">Closed</th>
            </tr></thead>
            <tbody>
              {data.orders.map((o, i) => {
                const vColor = o.variance === 0 ? 'text-[#10B981]' : o.variance > 0 ? 'text-[#b56b38]' : 'text-[#EF4444]';
                return (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`cash-order-${i}`}>
                    <td className="px-4 py-3 text-[#b56b38] font-mono font-medium">{o.order_number}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${o.fulfillment_type === 'pickup' ? 'bg-[#b56b38]/20 text-[#b56b38]' : 'bg-amber-500/20 text-amber-400'}`}>{o.fulfillment_type}</span></td>
                    <td className="px-4 py-3 text-white">{o.customer_name || '—'}</td>
                    <td className="px-4 py-3 text-[#94A3B8]">{o.collector}</td>
                    <td className="px-4 py-3 text-right text-white">${o.expected.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-white">${o.collected.toFixed(2)}</td>
                    <td className={`px-4 py-3 text-right font-medium ${vColor}`}>{o.variance >= 0 ? '+' : ''}${o.variance.toFixed(2)}</td>
                    <td className="px-4 py-3 text-[#64748B] text-xs">{o.closed_at ? new Date(o.closed_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : '—'}</td>
                  </tr>
                );
              })}
              {data.orders.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#64748B]">No COD orders closed on this date</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="bg-[#12151C] rounded-xl h-24 animate-pulse" />
      ))}
    </div>
  );
}
